import { governedProviderSpend } from "./governedProviderEvidence";
import {
  addCalendarDays,
  calendarDaysBetween,
  isCalendarDate,
  reportingDateOf,
  reportingDayStartMs,
} from "./reportingCalendar";
import { normalizeProviderSurfaceKey } from "./providerSurfaceRegistry";
import { formatFlightRange } from "./flightDates";
import { isInternalReportingExcludedCampaign } from "./canonicalCampaignIdentity";

const APPROVED_PLAN_STATUSES = new Set([
  "APPROVED",
  "COMPLETED",
  "EXECUTED",
  "COUNTERSIGNED",
]);

const DAY_MS = 24 * 60 * 60 * 1000;

const normalizeCampaignName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeUpper = (value) => String(value || "").trim().toUpperCase();
const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
const PROGRAMMATIC_PLATFORM_KEYS = new Set([
  "audio",
  "ingame",
  "ctv",
  "video",
  "mobile",
  "display",
  "dooh",
  "email",
  // ChatGPT Search inventory is activated + reported through StackAdapt.
  "chatgptsearch",
]);
const DIRECT_CLIENT_SPEND_KEYS = new Set([
  "meta",
  "facebook",
  "instagram",
  "audience_network",
  "messenger",
  "tiktok",
  "youtube",
  "x",
  "truthsocial",
  "rumble",
  "reddit",
  "googlesearch",
]);

const pickFirst = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

export const parseBudgetValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 0 ? value : null;
  }

  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.-]/g, "");
    if (!cleaned) return null;
    const parsed = Number.parseFloat(cleaned);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  return null;
};

const normalizeProgrammaticKey = (value) =>
  normalizeProviderSurfaceKey(value) || String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const PLATFORM_PACING_LABELS = Object.freeze({
  facebook: "Facebook",
  instagram: "Instagram",
  meta: "Meta (Facebook + Instagram)",
  tiktok: "TikTok",
  youtube: "YouTube",
  x: "X",
  truthsocial: "Truth Social",
  rumble: "Rumble",
  googlesearch: "Google Search",
  chatgptsearch: "ChatGPT Search",
  reddit: "Reddit",
  amazon: "Amazon",
  yahoo_dsp: "Yahoo!",
  viant: "Viant",
  audio: "Audio",
  ingame: "In-Game",
  ctv: "CTV",
  video: "Video",
  mobile: "Mobile",
  display: "Display",
  dooh: "DOOH",
  email: "Email",
});

const normalizePlanPlatformKey = (value) => {
  const providerKey = normalizeProviderSurfaceKey(value);
  if (providerKey) return providerKey;
  const raw = String(value || "").trim();
  const compact = normalizeKey(raw);
  if (!compact) return "";

  if (
    compact === "meta" ||
    compact === "facebookinstagram" ||
    compact === "metafacebookinstagram" ||
    (compact.includes("facebook") && compact.includes("instagram"))
  ) {
    return "meta";
  }
  if (
    ["facebook", "fb", "metafacebook"].includes(compact) ||
    (compact.includes("facebook") && !compact.includes("instagram"))
  ) {
    return "facebook";
  }
  if (
    ["instagram", "ig", "insta", "metainstagram"].includes(compact) ||
    (compact.includes("instagram") && !compact.includes("facebook"))
  ) {
    return "instagram";
  }
  if (["tiktok", "tt"].includes(compact)) return "tiktok";
  if (
    [
      "youtube",
      "youtubevideo",
      "googleyoutube",
      "googleyoutubevideo",
      "googlevideo",
      "yt",
      "ytvideo",
    ].includes(compact)
  ) {
    return "youtube";
  }
  if (["x", "twitter", "xads"].includes(compact)) return "x";
  if (["truthsocial", "truth"].includes(compact)) return "truthsocial";
  if (compact === "rumble") return "rumble";
  if (compact.includes("chatgpt") || compact.includes("openai")) return "chatgptsearch";
  if (["googlesearch", "googlepaidsearch", "paidsearch", "sem", "search"].includes(compact)) return "googlesearch";
  if (compact === "reddit") return "reddit";
  if (["amazon", "amazonads", "amazondsp", "amazondisplay", "amazonvideo"].includes(compact)) return "amazon";
  if (compact === "audio") return "audio";
  if (["ingame", "ingameads"].includes(compact)) return "ingame";
  if (["ctv", "connectedtv", "connectedtelevision", "ott"].includes(compact)) return "ctv";
  if (["video", "olv", "onlinevideo"].includes(compact)) return "video";
  if (compact === "mobile") return "mobile";
  if (["display", "banner", "banners"].includes(compact)) return "display";
  if (["dooh", "digitaloutofhome"].includes(compact)) return "dooh";
  if (["email", "mail"].includes(compact)) return "email";

  return compact;
};

const getPlatformPacingLabel = (platformKey, fallback = "") =>
  PLATFORM_PACING_LABELS[platformKey] || String(fallback || "").trim() || "Platform";

const TEXT_ALLOCATION_PLATFORM_LABELS = [
  "Meta (Facebook + Instagram)",
  "Facebook + Instagram",
  "Meta / Facebook",
  "Meta / Instagram",
  "Google YouTube Video",
  "Google YouTube",
  "YouTube Video",
  "Google Video",
  "Online Video",
  "Truth Social",
  "Google Search",
  "Connected TV",
  "Facebook",
  "Instagram",
  "YouTube",
  "TikTok",
  "Rumble",
  "Reddit",
  "Amazon",
  "Yahoo! DSP",
  "Yahoo DSP",
  "Yahoo!",
  "Yahoo",
  "Viant DSP (Display)",
  "Viant DSP",
  "Viant",
  "Audio",
  "In-Game",
  "Display",
  "Mobile",
  "Video",
  "DOOH",
  "Email",
  "CTV",
  "OLV",
  "Meta",
  "X",
].sort((a, b) => b.length - a.length);

const escapeRegExp = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const parsePercentValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value > 0 && value <= 1) return value;
    if (value > 1 && value <= 100) return value / 100;
  }

  if (typeof value === "string") {
    const cleaned = value.replace(/[^0-9.-]/g, "");
    if (!cleaned) return null;
    const parsed = Number.parseFloat(cleaned);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed <= 1 ? parsed : parsed / 100;
  }

  return null;
};

const collectPlanTextSources = (planInput) =>
  [
    planInput?.strategyText,
    planInput?.strategy_text,
    planInput?.strategyPayload?.strategyText,
    planInput?.strategyPayload?.strategy_text,
    planInput?.strategy_payload?.strategyText,
    planInput?.strategy_payload?.strategy_text,
    planInput?.rawCampaign?.strategy_text,
    planInput?.rawCampaign?.strategyText,
  ].filter((value) => typeof value === "string" && value.trim());

const extractPercentFromText = (value) => {
  const match = String(value || "").match(/(\d+(?:\.\d+)?)\s*%/);
  return match ? parsePercentValue(match[0]) : null;
};

const normalizeTextAllocationLine = (value) =>
  String(value || "")
    .replace(/^[\s\-*•]+/, "")
    .trim();

const buildTextAllocationRow = ({ platformLabel, allocationPct, pacingBudget }) => {
  const platformKey = normalizePlanPlatformKey(platformLabel);
  if (!platformKey || allocationPct == null || typeof pacingBudget !== "number") return null;
  const plannedBudget = pacingBudget * allocationPct;
  if (typeof plannedBudget !== "number" || !Number.isFinite(plannedBudget) || plannedBudget <= 0) {
    return null;
  }
  return {
    platformKey,
    label: getPlatformPacingLabel(platformKey, platformLabel),
    plannedBudget,
    allocationPct,
  };
};

const extractTextPlannedPlatformAllocations = (text, pacingBudget) => {
  if (typeof text !== "string" || typeof pacingBudget !== "number") return [];

  const rows = [];
  const seen = new Set();
  const pushRow = (platformLabel, allocationPct) => {
    const row = buildTextAllocationRow({ platformLabel, allocationPct, pacingBudget });
    if (!row) return;
    const signature = `${row.platformKey}|${row.allocationPct}`;
    if (seen.has(signature)) return;
    seen.add(signature);
    rows.push(row);
  };

  const lines = text
    .replace(/\r/g, "\n")
    .split("\n")
    .map(normalizeTextAllocationLine)
    .filter(Boolean);

  lines.forEach((line, index) => {
    TEXT_ALLOCATION_PLATFORM_LABELS.forEach((platformLabel) => {
      const labelPattern = escapeRegExp(platformLabel);
      const sameLineMatch = line.match(new RegExp(`^${labelPattern}(?:\\b|\\s|[:\\-–—])([\\s\\S]*)$`, "i"));
      if (sameLineMatch) {
        const allocationPct = extractPercentFromText(sameLineMatch[1]);
        if (allocationPct != null) {
          pushRow(platformLabel, allocationPct);
          return;
        }
      }

      if (normalizeKey(line) !== normalizeKey(platformLabel)) return;
      const nextText = [lines[index + 1], lines[index + 2]].filter(Boolean).join(" ");
      const allocationPct = extractPercentFromText(nextText);
      if (allocationPct != null) pushRow(platformLabel, allocationPct);
    });
  });

  return rows;
};

const collectAllocationArrays = (input, visited = new Set(), depth = 0) => {
  if (!input || depth > 3) return [];
  if (Array.isArray(input)) return [input];
  if (typeof input !== "object" || visited.has(input)) return [];
  visited.add(input);

  const arrays = [];
  const directKeys = [
    "allocations",
    "allocation",
    "platformAllocation",
    "platform_allocation",
    "platformAllocations",
    "platform_allocations",
    "budgetAllocation",
    "budget_allocation",
    "budgetAllocations",
    "budget_allocations",
    "channelSplit",
    "channel_split",
    "channels",
    "platforms",
    "mediaMix",
    "media_mix",
    "items",
    "rows",
  ];

  directKeys.forEach((key) => {
    if (Array.isArray(input[key])) arrays.push(input[key]);
  });

  Object.values(input).forEach((value) => {
    if (value && typeof value === "object") {
      arrays.push(...collectAllocationArrays(value, visited, depth + 1));
    }
  });

  return arrays;
};

const normalizeAllocationRow = (row, pacingBudget) => {
  if (!row || typeof row !== "object") return null;

  const rawPlatform = pickFirst(
    row.platform,
    row.platformLabel,
    row.platform_label,
    row.label,
    row.name,
    row.title,
    row.channel,
    row.channelLabel,
    row.channel_label,
    row.lineItemKey,
    row.line_item_key,
    row.key
  );
  const platformKey = normalizePlanPlatformKey(rawPlatform);
  if (!platformKey) return null;

  const explicitBudget = parseBudgetValue(
    pickFirst(
      row.budget,
      row.budgetLabel,
      row.budget_label,
      row.amount,
      row.spend,
      row.plannedBudget,
      row.planned_budget,
      row.plannedSpend,
      row.planned_spend,
      row.budgetAmount,
      row.budget_amount
    )
  );
  const allocationPct = parsePercentValue(
    pickFirst(
      row.percent,
      row.percentage,
      row.allocationPercent,
      row.allocation_percent,
      row.allocationPct,
      row.allocation_pct,
      row.budgetPercent,
      row.budget_percent,
      row.budgetShare,
      row.budget_share,
      row.share,
      row.allocation,
      row.allocationLabel,
      row.allocation_label,
      row.value
    )
  );
  const plannedBudget =
    explicitBudget ??
    (allocationPct != null && typeof pacingBudget === "number"
      ? pacingBudget * allocationPct
      : null);

  if (typeof plannedBudget !== "number" || !Number.isFinite(plannedBudget) || plannedBudget <= 0) {
    return null;
  }

  return {
    platformKey,
    label: getPlatformPacingLabel(platformKey, rawPlatform),
    plannedBudget,
    allocationPct,
  };
};

const extractPlannedPlatformAllocations = (planInput, pacingBudget) => {
  const sources = [
    planInput?.budgetBreakdown,
    planInput?.budget_breakdown,
    planInput?.strategyPayloadContract,
    planInput?.strategy_payload_contract,
    planInput?.strategyPayload,
    planInput?.strategy_payload,
    planInput?.financialSummary,
    planInput?.financial_summary,
    planInput?.rawCampaign?.budget_breakdown,
    planInput?.rawCampaign?.budgetBreakdown,
    planInput?.rawCampaign?.strategy_payload_contract,
    planInput?.rawCampaign?.strategyPayloadContract,
    planInput?.rawCampaign?.strategy_payload,
    planInput?.rawCampaign?.strategyPayload,
    planInput,
  ].filter(Boolean);

  const rows = [];
  const seenRows = new WeakSet();
  const seenSignatures = new Set();
  sources.forEach((source) => {
    collectAllocationArrays(source).forEach((array) => {
      array.forEach((row) => {
        if (row && typeof row === "object") {
          if (seenRows.has(row)) return;
          seenRows.add(row);
        }
        const normalized = normalizeAllocationRow(row, pacingBudget);
        if (!normalized) return;
        const signature = [
          normalized.platformKey,
          normalized.plannedBudget,
          normalized.allocationPct ?? "",
          normalized.label,
        ].join("|");
        if (seenSignatures.has(signature)) return;
        seenSignatures.add(signature);
        rows.push(normalized);
      });
    });
  });

  collectPlanTextSources(planInput).forEach((text) => {
    extractTextPlannedPlatformAllocations(text, pacingBudget).forEach((row) => {
      const signature = [
        row.platformKey,
        row.plannedBudget,
        row.allocationPct ?? "",
        row.label,
      ].join("|");
      if (seenSignatures.has(signature)) return;
      seenSignatures.add(signature);
      rows.push(row);
    });
  });

  const byKey = new Map();
  rows.forEach((row) => {
    const existing = byKey.get(row.platformKey);
    if (existing) {
      byKey.set(row.platformKey, {
        ...existing,
        plannedBudget: existing.plannedBudget + row.plannedBudget,
        allocationPct:
          existing.allocationPct != null && row.allocationPct != null
            ? existing.allocationPct + row.allocationPct
            : existing.allocationPct ?? row.allocationPct,
      });
    } else {
      byKey.set(row.platformKey, row);
    }
  });

  return Array.from(byKey.values());
};

export const resolvePlannedPlatformAllocation = ({
  planInput,
  platformKey,
  pacingBudget,
} = {}) => {
  const selectedKey = normalizePlanPlatformKey(platformKey);
  if (!selectedKey || selectedKey === "all") return null;

  const allocations = extractPlannedPlatformAllocations(planInput, pacingBudget);
  if (!allocations.length) return null;

  const exact = allocations.find((row) => row.platformKey === selectedKey);
  if (exact) {
    return {
      ...exact,
      basis: "platform_allocation",
      isGroupedMetaAllocation: false,
    };
  }

  if (selectedKey === "meta") {
    const metaFamilyRows = allocations.filter((row) =>
      ["meta", "facebook", "instagram"].includes(row.platformKey)
    );
    if (metaFamilyRows.length) {
      return {
        platformKey: "meta",
        label: getPlatformPacingLabel("meta"),
        plannedBudget: metaFamilyRows.reduce((sum, row) => sum + row.plannedBudget, 0),
        allocationPct: metaFamilyRows.every((row) => row.allocationPct != null)
          ? metaFamilyRows.reduce((sum, row) => sum + row.allocationPct, 0)
          : null,
        basis: "platform_allocation",
        isGroupedMetaAllocation: metaFamilyRows.some((row) => row.platformKey === "meta"),
      };
    }
  }

  if (selectedKey === "facebook" || selectedKey === "instagram") {
    const groupedMeta = allocations.find((row) => row.platformKey === "meta");
    if (groupedMeta) {
      return {
        ...groupedMeta,
        label: getPlatformPacingLabel("meta"),
        basis: "grouped_meta_allocation",
        isGroupedMetaAllocation: true,
      };
    }
  }

  return null;
};

const findDeepValueByKeys = (input, targetKeys, depth = 0, visited = new Set()) => {
  if (!input || depth > 4) return null;

  if (Array.isArray(input)) {
    for (const item of input) {
      const found = findDeepValueByKeys(item, targetKeys, depth + 1, visited);
      if (found !== null && found !== undefined && found !== "") return found;
    }
    return null;
  }

  if (typeof input !== "object") return null;
  if (visited.has(input)) return null;
  visited.add(input);

  for (const [key, value] of Object.entries(input)) {
    if (targetKeys.has(normalizeKey(key)) && value !== null && value !== undefined && value !== "") {
      return value;
    }
  }

  for (const value of Object.values(input)) {
    const found = findDeepValueByKeys(value, targetKeys, depth + 1, visited);
    if (found !== null && found !== undefined && found !== "") return found;
  }

  return null;
};

/*
 * A flight date is an Eastern reporting-calendar day: it starts at Eastern
 * midnight and ends the instant before the next Eastern midnight, whatever
 * zone the viewer's device is in. (These were browser-local midnights, so the
 * same campaign's elapsed days and pacing moved with the device, and a
 * spring-forward week lost a day.) A Date instance or other parseable value
 * is read by its UTC calendar date, the convention for DATEONLY values that
 * arrive as UTC-midnight instants.
 */
const flightCalendarDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }
  const raw = String(value).trim();
  if (!raw) return null;
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})(?:$|[T\s])/);
  if (match) return isCalendarDate(match[1]) ? match[1] : null;
  // A non-ISO date ("Oct 12, 2026") parses at device-local midnight; its
  // local components are the date as written, whatever the device zone.
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return null;
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const parseDateBoundaryMs = (value, boundary = "start") => {
  const day = flightCalendarDate(value);
  if (!day) return null;
  if (boundary === "end") {
    const next = reportingDayStartMs(addCalendarDays(day, 1));
    return next == null ? null : next - 1;
  }
  return reportingDayStartMs(day);
};

const toDateOnlyString = (value) => flightCalendarDate(value);

// Inclusive count of Eastern calendar days touched by [startMs, endMs].
const inclusiveCalendarDays = (startMs, endMs) => {
  if (!startMs || !endMs || endMs < startMs) return null;
  const days = calendarDaysBetween(reportingDateOf(startMs), reportingDateOf(endMs));
  return days == null ? null : Math.max(1, days + 1);
};

export const deriveActiveFlightDayCount = ({
  flightStart,
  flightEnd,
  nowMs = Date.now(),
} = {}) => {
  const startMs = parseDateBoundaryMs(flightStart, "start");
  const endMs = parseDateBoundaryMs(flightEnd, "end");
  if (!startMs || !endMs || endMs < startMs) return null;

  const activeEndMs = Math.min(
    Number.isFinite(nowMs) ? nowMs : Date.now(),
    endMs
  );
  const activeDays = inclusiveCalendarDays(startMs, activeEndMs);
  return Math.max(1, Math.min(activeDays || 1, inclusiveCalendarDays(startMs, endMs) || 1));
};

export const deriveDailyPacingStatus = ({
  actualDailySpend,
  targetDailySpend,
} = {}) => {
  if (
    typeof actualDailySpend !== "number" ||
    !Number.isFinite(actualDailySpend) ||
    typeof targetDailySpend !== "number" ||
    !Number.isFinite(targetDailySpend) ||
    targetDailySpend <= 0
  ) {
    return {
      dailyPacingPctVsTarget: null,
      dailyPacingStatus: "unavailable",
      dailyPacingLabel: "Unavailable",
      dailyPacingTone: "neutral",
    };
  }

  const dailyPacingPctVsTarget = (actualDailySpend / targetDailySpend) * 100;
  if (dailyPacingPctVsTarget < 90) {
    return {
      dailyPacingPctVsTarget,
      dailyPacingStatus: "behind pace",
      dailyPacingLabel: "Behind Pace",
      dailyPacingTone: "bad",
    };
  }
  if (dailyPacingPctVsTarget > 110) {
    return {
      dailyPacingPctVsTarget,
      dailyPacingStatus: "ahead of pace",
      dailyPacingLabel: "Ahead of Pace",
      dailyPacingTone: "good",
    };
  }
  return {
    dailyPacingPctVsTarget,
    dailyPacingStatus: "on pace",
    dailyPacingLabel: "On Pace",
    dailyPacingTone: "ok",
  };
};

const extractCurveSegments = (budgetBreakdown, totalBudget) => {
  if (!budgetBreakdown || typeof budgetBreakdown !== "object") return [];

  const arraysToTry = [
    budgetBreakdown,
    budgetBreakdown.phases,
    budgetBreakdown.segments,
    budgetBreakdown.items,
    budgetBreakdown.allocations,
    budgetBreakdown.weeks,
    budgetBreakdown.weekly,
    budgetBreakdown.daily,
    budgetBreakdown.flighting,
    budgetBreakdown.flightingPhases,
  ];

  for (const candidate of arraysToTry) {
    if (!Array.isArray(candidate)) continue;

    const normalized = candidate
      .map((row) => {
        const start = pickFirst(
          row.start,
          row.startDate,
          row.start_date,
          row.flightStart,
          row.flight_start_date,
          row.weekStart,
          row.week_start
        );
        const end = pickFirst(
          row.end,
          row.endDate,
          row.end_date,
          row.flightEnd,
          row.flight_end_date,
          row.weekEnd,
          row.week_end
        );

        const startMs = parseDateBoundaryMs(start, "start");
        const endMs = parseDateBoundaryMs(end, "end");
        if (!startMs || !endMs || endMs < startMs) return null;

        const explicitBudget = parseBudgetValue(
          pickFirst(
            row.budget,
            row.amount,
            row.total,
            row.plannedSpend,
            row.planned_spend,
            row.budgetAmount,
            row.budget_amount
          )
        );

        const pct = parsePercentValue(
          pickFirst(
            row.percent,
            row.percentage,
            row.allocationPct,
            row.allocation_pct,
            row.weight
          )
        );

        const derivedBudget =
          explicitBudget ??
          (pct != null && typeof totalBudget === "number" ? totalBudget * pct : null);

        if (typeof derivedBudget !== "number" || !Number.isFinite(derivedBudget) || derivedBudget <= 0) {
          return null;
        }

        return {
          startMs,
          endMs,
          budget: derivedBudget,
        };
      })
      .filter(Boolean);

    if (normalized.length) return normalized;
  }

  return [];
};

const computeCurvePlannedSpend = (segments, nowMs) => {
  if (!Array.isArray(segments) || !segments.length) return null;

  let total = 0;

  for (const segment of segments) {
    const duration = Math.max(1, segment.endMs - segment.startMs);

    if (nowMs <= segment.startMs) {
      continue;
    }

    if (nowMs >= segment.endMs) {
      total += segment.budget;
      continue;
    }

    const elapsed = Math.max(0, Math.min(1, (nowMs - segment.startMs) / duration));
    total += segment.budget * elapsed;
  }

  return Number.isFinite(total) ? total : null;
};

const getBreakdownTotal = (budgetBreakdown) => {
  if (!budgetBreakdown || typeof budgetBreakdown !== "object") {
    return { total: null, hasNamedTotal: false };
  }

  const namedTotal = parseBudgetValue(
    findDeepValueByKeys(budgetBreakdown, new Set(["totalbudget", "grossbudget", "approvedbudget", "overallbudget"]))
  );
  if (namedTotal) return { total: namedTotal, hasNamedTotal: true };

  const explicitSegments = extractCurveSegments(budgetBreakdown, null);
  if (!explicitSegments.length) return { total: null, hasNamedTotal: false };

  const total = explicitSegments.reduce((sum, segment) => sum + (segment.budget || 0), 0);
  return {
    total: Number.isFinite(total) && total > 0 ? total : null,
    hasNamedTotal: false,
  };
};

const resolvePlanBudgetStructure = (
  sourcePlan,
  { allowMarginAssumption = false, defaultMarginPercent = null } = {}
) => {
  const financialObjects = [
    sourcePlan?.financialSummary,
    sourcePlan?.financial_summary,
    sourcePlan?.financials,
    sourcePlan?.finance,
    sourcePlan?.budget,
    sourcePlan?.budgets,
    sourcePlan?.pricing,
    sourcePlan?.summary,
    sourcePlan?.deal,
    sourcePlan?.contract,
    sourcePlan?.strategyPayloadFinancial,
    sourcePlan?.strategy_payload_financial,
    sourcePlan?.strategyPayloadContract,
    sourcePlan?.strategy_payload_contract,
    sourcePlan?.strategyPayload,
    sourcePlan?.strategy_payload,
    sourcePlan,
    sourcePlan?.rawCampaign?.financials,
    sourcePlan?.rawCampaign?.finance,
    sourcePlan?.rawCampaign?.budget,
    sourcePlan?.rawCampaign?.budgets,
    sourcePlan?.rawCampaign?.pricing,
    sourcePlan?.rawCampaign?.summary,
    sourcePlan?.rawCampaign?.deal,
    sourcePlan?.rawCampaign?.contract,
    sourcePlan?.rawCampaign?.financial_summary,
    sourcePlan?.rawCampaign?.strategy_payload_financial,
    sourcePlan?.rawCampaign?.strategyPayloadFinancial,
    sourcePlan?.rawCampaign?.strategyPayloadContract,
    sourcePlan?.rawCampaign?.strategy_payload_contract,
    sourcePlan?.rawCampaign?.strategyPayload,
    sourcePlan?.rawCampaign?.strategy_payload,
    sourcePlan?.rawCampaign,
    sourcePlan?.budgetBreakdown,
  ].filter(Boolean);

  const grossKeys = new Set([
    "grossbudget",
    "grossplanbudget",
    "approvedbudget",
    "approvedtotalbudget",
    "campaignbudget",
    "totalbudget",
    "budgettotal",
  ]);
  const netKeys = new Set([
    "netmediabudget",
    "approvednetmediabudget",
    "netbudget",
    "medianetbudget",
    "deployablemediabudget",
    "netspendbudget",
    "workingmediabudget",
    "workingbudget",
    "netmediatotal",
  ]);
  const marginDollarKeys = new Set([
    "margindollars",
    "marginamount",
    "profitdollars",
    "profitamount",
    "retainedfee",
    "retainedamount",
    "agencyfee",
    "feeamount",
  ]);
  const marginPercentKeys = new Set([
    "targetmarginpercent",
    "targetmarginpercentage",
    "targetmarginpct",
    "authorizedtargetmarginpercent",
    "authorizedtargetmarginpercentage",
    "authorizedtargetmarginpct",
    "marginpercent",
    "marginpercentage",
    "marginrate",
    "profitpercent",
    "profitpercentage",
    "profitrate",
    "retainedfeepercent",
    "retainedfeepercentage",
    "retainedpercent",
    "retainedpercentage",
    "agencyfeepercent",
    "agencyfeepercentage",
    "sharepercent",
    "sharepercentage",
    "sharepct",
    "share",
    "marginpct",
    "profitpct",
  ]);

  let grossBudget = parseBudgetValue(sourcePlan?.totalBudget);
  let budgetSource = grossBudget ? "approved_total_budget" : null;
  let grossBudgetSource = grossBudget ? "approved campaign total_budget" : null;

  if (!grossBudget) {
    for (const obj of financialObjects) {
      const found = parseBudgetValue(findDeepValueByKeys(obj, grossKeys));
      if (typeof found === "number") {
        grossBudget = found;
        budgetSource = "financial_summary";
        grossBudgetSource = "approved financial summary gross budget";
        break;
      }
    }
  }

  const breakdownInfo = getBreakdownTotal(sourcePlan?.budgetBreakdown);
  const breakdownTotal = breakdownInfo.total;
  let usedBreakdownAsFullBudget = false;
  if (typeof breakdownTotal === "number" && !grossBudget) {
    grossBudget = breakdownTotal;
    budgetSource = "budget_breakdown_total";
    grossBudgetSource = breakdownInfo.hasNamedTotal
      ? "approved budget breakdown named total"
      : "budget breakdown explicit segment total";
    usedBreakdownAsFullBudget = true;
  }

  let netMediaBudget = null;
  let marginDollars = null;
  let marginPercent = null;
  let targetMarginPercent = null;
  let netBudgetSource = null;
  let marginSource = null;
  let usedMarginAssumption = false;
  let assumedMarginPercent = null;

  for (const obj of financialObjects) {
    if (targetMarginPercent == null) {
      const found = parsePercentValue(
        findDeepValueByKeys(
          obj,
          new Set([
            "targetmarginpercent",
            "targetmarginpercentage",
            "targetmarginpct",
            "authorizedtargetmarginpercent",
            "authorizedtargetmarginpercentage",
            "authorizedtargetmarginpct",
          ])
        )
      );
      if (typeof found === "number") {
        targetMarginPercent = found;
      }
    }
    if (netMediaBudget == null) {
      const found = parseBudgetValue(findDeepValueByKeys(obj, netKeys));
      if (typeof found === "number" && found > 0) {
        netMediaBudget = found;
        netBudgetSource = "explicit approved net media budget";
      }
    }
    if (marginDollars == null) {
      const found = parseBudgetValue(findDeepValueByKeys(obj, marginDollarKeys));
      if (typeof found === "number") {
        marginDollars = found;
        marginSource = "explicit approved margin dollars";
      }
    }
    if (marginPercent == null) {
      const found = parsePercentValue(findDeepValueByKeys(obj, marginPercentKeys));
      if (typeof found === "number") {
        marginPercent = found;
        if (!marginSource) marginSource = "explicit approved margin/share percent";
      }
    }
  }

  if (typeof grossBudget === "number" && typeof netMediaBudget === "number" && netMediaBudget > grossBudget) {
    netMediaBudget = null;
    netBudgetSource = null;
  }

  if (marginDollars == null && typeof grossBudget === "number" && typeof netMediaBudget === "number") {
    marginDollars = Math.max(0, grossBudget - netMediaBudget);
    if (!marginSource) {
      marginSource = "derived from approved gross budget minus explicit net media budget";
    }
  }

  if (marginPercent == null && typeof grossBudget === "number" && grossBudget > 0 && typeof marginDollars === "number") {
    marginPercent = Math.max(0, Math.min(1, marginDollars / grossBudget));
    if (!marginSource) marginSource = "derived from approved gross budget and margin dollars";
  }

  const pacingBudget = typeof grossBudget === "number" ? grossBudget : null;

  const calculationReason =
    typeof grossBudget === "number"
      ? "Pacing uses the approved client-facing budget."
      : "No reliable approved budget basis was available for pacing.";

  return {
    grossBudget: typeof grossBudget === "number" ? grossBudget : null,
    netMediaBudget: typeof netMediaBudget === "number" ? netMediaBudget : null,
    marginDollars: typeof marginDollars === "number" ? marginDollars : null,
    marginPercent: typeof marginPercent === "number" ? marginPercent : null,
    targetMarginPercent: typeof targetMarginPercent === "number" ? targetMarginPercent : null,
    budgetSource,
    grossBudgetSource,
    netBudgetSource,
    marginSource,
    breakdownTotal: typeof breakdownTotal === "number" ? breakdownTotal : null,
    usedBreakdownAsFullBudget,
    usedMarginAssumption,
    assumedMarginPercent,
    pacingBudget,
    pacingTargetLabel: typeof grossBudget === "number" ? "Approved Budget" : null,
    calculationReason,
  };
};

export const isApprovedPlanCampaign = (campaign) => {
  if (!campaign || typeof campaign !== "object") return false;

  // Internal supervised-flight authorization/proof rows carry an APPROVED
  // status but are evidence artifacts, not plans. They must never become the
  // plan context (which drives the "Auto —" campaign label and the Targeting &
  // Flight Specs panel), even when they are the only APPROVED row.
  if (isInternalReportingExcludedCampaign(campaign)) return false;

  if (campaign.countersigned_at || campaign.countersignedAt) return true;

  const statuses = [
    campaign.contract_status,
    campaign.contractStatus,
    campaign.authorization_status,
    campaign.authorizationStatus,
    campaign.status,
  ];

  return statuses.some((status) => APPROVED_PLAN_STATUSES.has(normalizeUpper(status)));
};

const namesMatch = (left, right) => {
  const a = normalizeCampaignName(left);
  const b = normalizeCampaignName(right);
  if (!a || !b) return false;
  if (a === b) return true;
  return a.includes(b) || b.includes(a);
};

const normalizeCampaignIdentity = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  return text && text !== "null" && text !== "undefined" ? text : "";
};

const readCampaignIdentity = (campaign) =>
  normalizeCampaignIdentity(
    campaign?.id ?? campaign?.campaign_id ?? campaign?.campaignId ?? campaign?.mediaCampaignId
  );

export const selectApprovedPlanCampaign = ({
  campaigns,
  selectedCampaignId = null,
  selectedCampaignName,
  nowMs = Date.now(),
  allowAdvertiserBestFit = true,
  allowSingleApprovedFallback = false,
}) => {
  const approved = (Array.isArray(campaigns) ? campaigns : []).filter(isApprovedPlanCampaign);
  if (!approved.length) return null;

  /*
   * Governed identity first.
   *
   * A campaign's id is what it IS; its name is what it is CALLED. Matching on the
   * name meant renaming a campaign silently orphaned its approved plan — the
   * planner row and the selector disagreed for as long as one of them was stale,
   * and Optimus reported PLAN MISSING for a plan that existed. Identity is checked
   * before any name so a rename can never be a failure mode.
   */
  const wantedId = normalizeCampaignIdentity(selectedCampaignId);
  if (wantedId) {
    const byId = approved.find((campaign) => readCampaignIdentity(campaign) === wantedId);
    if (byId) {
      return {
        ...byId,
        __matchType: "exact_id",
        __matchConfidence: "high",
        __matchDetail:
          "Approved plan matched the selected campaign by governed campaign identity.",
      };
    }
  }

  if (selectedCampaignName) {
    const exact = approved.find((campaign) =>
      normalizeCampaignName(campaign.campaign_name || campaign.campaignName) === normalizeCampaignName(selectedCampaignName)
    );
    if (exact) {
      return {
        ...exact,
        __matchType: "exact_name",
        __matchConfidence: "high",
        __matchDetail: "Approved plan matched the selected live campaign by exact normalized name.",
      };
    }

    const fuzzy = approved.find((campaign) =>
      namesMatch(campaign.campaign_name || campaign.campaignName, selectedCampaignName)
    );
    if (fuzzy) {
      return {
        ...fuzzy,
        __matchType: "fuzzy_name",
        __matchConfidence: "medium",
        __matchDetail: "Approved plan matched the selected live campaign by similar normalized campaign name.",
      };
    }

    /*
     * Neither identity nor name resolved. Fall through ONLY when the advertiser
     * has exactly one approved campaign, where there is nothing to be ambiguous
     * about — otherwise fail closed rather than attach the selected campaign to
     * some other campaign's approved plan.
     */
    if (allowSingleApprovedFallback && approved.length === 1) {
      return {
        ...approved[0],
        __matchType: "single_approved_campaign",
        __matchConfidence: "medium",
        __matchDetail:
          "Approved plan selected because it is the advertiser's only approved campaign; neither identity nor name matched.",
      };
    }

    return null;
  }

  if (!allowAdvertiserBestFit) {
    if (allowSingleApprovedFallback && approved.length === 1) {
      return {
        ...approved[0],
        __matchType: "single_approved_campaign",
        __matchConfidence: "high",
        __matchDetail: "Approved plan selected because it is the only approved campaign available for this advertiser.",
      };
    }
    return null;
  }

  const scored = approved
    .map((campaign) => {
      const startMs = parseDateBoundaryMs(
        pickFirst(campaign.flight_start_date, campaign.flightStart, campaign.flight_start),
        "start"
      );
      const endMs = parseDateBoundaryMs(
        pickFirst(campaign.flight_end_date, campaign.flightEnd, campaign.flight_end),
        "end"
      );

      let score = 0;
      if (campaign.countersigned_at || campaign.countersignedAt) score += 200;
      if (startMs && endMs && nowMs >= startMs && nowMs <= endMs) score += 100;
      else if (startMs && nowMs < startMs) score += 50;
      else if (endMs && nowMs > endMs) score += 25;

      if (parseBudgetValue(campaign.total_budget || campaign.totalBudget)) score += 15;
      if (startMs && endMs) score += 15;

      const recency = parseDateBoundaryMs(
        pickFirst(campaign.updated_at, campaign.updatedAt, campaign.created_at, campaign.createdAt),
        "end"
      );

      return { campaign, score, recency: recency || 0 };
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return b.recency - a.recency;
    });
  if (!scored[0]?.campaign) return null;
  return {
    ...scored[0].campaign,
    __matchType: "advertiser_best_fit",
    __matchConfidence: "medium",
    __matchDetail: "Approved plan selected as the best advertiser-level fit because no live campaign was selected.",
  };
};

export const extractFlightRange = (input) => {
  if (!input || typeof input !== "object") return { date_start: null, date_end: null };

  const pick = (...keys) => {
    for (const key of keys) {
      const value = input?.[key];
      if (value == null) continue;
      const normalized = String(value).trim();
      if (!normalized) continue;
      return normalized;
    }
    return null;
  };

  const nestedPick = (obj, ...keys) => {
    if (!obj || typeof obj !== "object") return null;
    for (const key of keys) {
      const value = obj?.[key];
      if (value == null) continue;
      const normalized = String(value).trim();
      if (!normalized) continue;
      return normalized;
    }
    return null;
  };

  let flightObj =
    input.flight ||
    input.flights ||
    input.flightDates ||
    input.flight_dates ||
    input.campaignFlight ||
    input.campaign_flight ||
    null;

  if (typeof flightObj === "string") {
    const raw = flightObj.trim();
    if (raw.startsWith("{") && raw.endsWith("}")) {
      try {
        flightObj = JSON.parse(raw);
      } catch (_error) {
        // Ignore malformed serialized flight payloads and keep falling back.
      }
    }
  }

  const dateStart =
    pick(
      "flight_start",
      "flightStart",
      "flight_start_date",
      "flightStartDate",
      "flightDateStart",
      "flight_date_start",
      "flight_from",
      "flightFrom",
      "date_start",
      "dateStart",
      "start_date",
      "startDate",
      "campaign_start",
      "campaignStart",
      "campaign_start_date",
      "campaignStartDate",
      "start",
      "start_at",
      "startAt"
    ) ||
    nestedPick(
      flightObj,
      "start",
      "start_date",
      "startDate",
      "date_start",
      "dateStart",
      "from",
      "flight_start",
      "flightStart",
      "flight_start_date",
      "flightStartDate"
    );

  const dateEnd =
    pick(
      "flight_end",
      "flightEnd",
      "flight_end_date",
      "flightEndDate",
      "flightDateEnd",
      "flight_date_end",
      "flight_to",
      "flightTo",
      "date_end",
      "dateEnd",
      "end_date",
      "endDate",
      "campaign_end",
      "campaignEnd",
      "campaign_end_date",
      "campaignEndDate",
      "end",
      "end_at",
      "endAt"
    ) ||
    nestedPick(
      flightObj,
      "end",
      "end_date",
      "endDate",
      "date_end",
      "dateEnd",
      "to",
      "flight_end",
      "flightEnd",
      "flight_end_date",
      "flightEndDate"
    );

  if (!dateStart || !dateEnd) return { date_start: null, date_end: null };
  return { date_start: dateStart, date_end: dateEnd };
};

export const diffDays = (start, end) => {
  const startDate = parseDateBoundaryMs(start, "start");
  const endDate = parseDateBoundaryMs(end, "start");
  if (!startDate || !endDate) return null;
  const delta = endDate - startDate;
  if (!Number.isFinite(delta)) return null;
  return Math.round(delta / DAY_MS);
};

/*
 * `selectedCampaignId` is the governed campaign identity of the current
 * selection and it is passed straight through, because `selectApprovedPlanCampaign`
 * checks identity BEFORE any name. That ordering is what stops two campaigns
 * sharing a name prefix — "Never Stop Chasing" and "Never Stop Chasing
 * Incremental Markets" — from resolving to each other's flight: the name path
 * ends in a substring comparison, and a caller that knows the id must never
 * reach it.
 */
export const resolveApprovedFlightTimeframe = ({
  campaigns,
  advertiser,
  selectedCampaignId = null,
  selectedCampaignName = null,
  allowAdvertiserBestFit = false,
  allowSingleApprovedFallback = true,
} = {}) => {
  const approvedFlightCampaign = selectApprovedPlanCampaign({
    campaigns,
    selectedCampaignId,
    selectedCampaignName,
    allowAdvertiserBestFit,
    allowSingleApprovedFallback,
  });

  const approvedFlightRange = extractFlightRange(approvedFlightCampaign);
  if (approvedFlightRange.date_start && approvedFlightRange.date_end) {
    const duration = diffDays(approvedFlightRange.date_start, approvedFlightRange.date_end);
    if (Number.isFinite(duration) && duration >= 0 && duration <= 120) {
      return { mode: "flight", ...approvedFlightRange, source: "approved_flight" };
    }
  }

  const advertiserFlightRange = extractFlightRange(advertiser);
  if (advertiserFlightRange.date_start && advertiserFlightRange.date_end) {
    const duration = diffDays(advertiserFlightRange.date_start, advertiserFlightRange.date_end);
    if (Number.isFinite(duration) && duration >= 0 && duration <= 120) {
      return { mode: "flight", ...advertiserFlightRange, source: "advertiser_flight" };
    }
  }

  return { mode: "preset", date_preset: "last_30d" };
};

export const formatApprovedFlightTimeframeLabel = (timeframe) => {
  if (timeframe?.mode === "flight" && timeframe.date_start && timeframe.date_end) {
    return formatFlightRange(timeframe.date_start, timeframe.date_end);
  }
  return "Flight not available";
};

const CREATIVE_ANALYSIS_PLATFORM_LABELS = {
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  google_search: "Google Search",
  chatgpt_search: "ChatGPT Search",
  display: "Display",
  ctv: "CTV",
  video: "Video",
  audio: "Audio",
  dooh: "DOOH",
  mobile: "Mobile",
  email: "Email",
  amazon: "Amazon",
  yahoo_dsp: "Yahoo!",
  viant: "Viant",
  reddit: "Reddit",
  shopify: "Shopify",
  linkedin: "LinkedIn Ads",
  x: "X",
  truth_social: "Truth Social",
  rumble: "Rumble",
  cinema: "Cinema",
  ingame: "In-Game",
};

const CREATIVE_ANALYSIS_PLATFORM_FIELD_KEYS = new Set([
  "platform",
  "platforms",
  "channel",
  "channels",
  "targetplatform",
  "targetplatforms",
  "selectedplatform",
  "selectedplatforms",
  "mediachannel",
  "mediachannels",
  "plannedchannel",
  "plannedchannels",
  "approvedchannel",
  "approvedchannels",
  "channelmix",
  "lineitemkey",
  "lineitemkeys",
  "placement",
  "placements",
  "inventory",
]);

const collectDeepValuesByKeys = (
  input,
  targetKeys,
  depth = 0,
  visited = new Set(),
  collected = []
) => {
  if (input == null || depth > 5) return collected;

  if (Array.isArray(input)) {
    input.forEach((item) =>
      collectDeepValuesByKeys(item, targetKeys, depth + 1, visited, collected)
    );
    return collected;
  }

  if (typeof input !== "object") return collected;
  if (visited.has(input)) return collected;
  visited.add(input);

  Object.entries(input).forEach(([key, value]) => {
    if (targetKeys.has(normalizeKey(key))) {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item == null || item === "") return;
          if (typeof item === "object") {
            collectDeepValuesByKeys(item, targetKeys, depth + 1, visited, collected);
            return;
          }
          collected.push(item);
        });
      } else if (value && typeof value === "object") {
        collectDeepValuesByKeys(value, targetKeys, depth + 1, visited, collected);
      } else if (value != null && value !== "") {
        collected.push(value);
      }
    }

    if (value && typeof value === "object") {
      collectDeepValuesByKeys(value, targetKeys, depth + 1, visited, collected);
    }
  });

  return collected;
};

const splitCreativeAnalysisPlatformTokens = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return [];

  const parts = raw
    .split(/\s*(?:,|\/|&|\+|\||\band\b)\s*/i)
    .map((part) => String(part || "").trim())
    .filter(Boolean);

  return parts.length ? parts : [raw];
};

export const normalizeCreativeAnalysisPlatformKeys = (value) => {
  const resolved = new Set();
  const push = (...keys) => {
    keys.forEach((key) => {
      const normalized = normalizeProgrammaticKey(key);
      if (normalized && CREATIVE_ANALYSIS_PLATFORM_LABELS[normalized]) {
        resolved.add(normalized);
      }
    });
  };

  splitCreativeAnalysisPlatformTokens(value).forEach((token) => {
    const normalized = normalizeProgrammaticKey(token);
    if (!normalized) return;

    if (
      normalized === "meta" ||
      normalized === "facebookinstagram" ||
      normalized === "instagramfacebook"
    ) {
      push("facebook", "instagram");
      return;
    }

    if (
      normalized === "paidsocial" ||
      normalized === "social" ||
      normalized === "socialmedia"
    ) {
      push("facebook", "instagram", "tiktok");
      return;
    }

    if (normalized.includes("truthsocial")) return push("truth_social");
    if (normalized === "x" || normalized.includes("twitter")) return push("x");
    if (normalized.includes("facebook")) return push("facebook");
    if (normalized.includes("instagram")) return push("instagram");
    if (normalized.includes("tiktok")) return push("tiktok");
    if (normalized.includes("youtube")) return push("youtube");

    if (normalized.includes("chatgpt") || normalized.includes("openai")) {
      return push("chatgpt_search");
    }

    if (
      normalized === "search" ||
      normalized.includes("googlesearch") ||
      normalized.includes("paidsearch") ||
      normalized === "sem"
    ) {
      return push("google_search");
    }

    if (normalized === "ctv" || normalized.includes("connectedtv")) return push("ctv");

    if (
      normalized === "dooh" ||
      normalized === "ooh" ||
      normalized.includes("digitaloutofhome") ||
      normalized.includes("outofhome")
    ) {
      return push("dooh");
    }

    if (
      normalized === "audio" ||
      normalized.includes("digitalaudio") ||
      normalized.includes("streamingaudio") ||
      normalized.includes("podcast")
    ) {
      return push("audio");
    }

    if (
      normalized === "video" ||
      normalized === "olv" ||
      normalized.includes("onlinevideo")
    ) {
      return push("video");
    }

    if (
      normalized === "mobile" ||
      normalized.includes("mobiledisplay") ||
      normalized.includes("appinstall")
    ) {
      return push("mobile");
    }

    if (
      normalized === "display" ||
      normalized.includes("programmaticdisplay") ||
      normalized.includes("banner")
    ) {
      return push("display");
    }

    if (normalized.includes("email")) return push("email");
    if (normalized.includes("amazon")) return push("amazon");
    if (normalized.includes("reddit")) return push("reddit");
    if (normalized.includes("rumble")) return push("rumble");
    if (normalized.includes("cinema")) return push("cinema");
    if (normalized.includes("ingame") || normalized.includes("gaming")) return push("ingame");

    if (CREATIVE_ANALYSIS_PLATFORM_LABELS[normalized]) {
      push(normalized);
    }
  });

  return Array.from(resolved);
};

export const getCreativeAnalysisPlatformLabel = (value) => {
  const normalized = normalizeProgrammaticKey(value);
  return CREATIVE_ANALYSIS_PLATFORM_LABELS[normalized] || null;
};

const resolveCreativeAnalysisPlatformOptionsFromInputs = (inputs = []) => {
  const seen = new Set();
  const options = [];

  inputs.forEach((input) => {
    const values = collectDeepValuesByKeys(
      input,
      CREATIVE_ANALYSIS_PLATFORM_FIELD_KEYS
    );

    values.forEach((value) => {
      normalizeCreativeAnalysisPlatformKeys(value).forEach((platformKey) => {
        if (seen.has(platformKey)) return;
        seen.add(platformKey);
        options.push({
          value: platformKey,
          label: getCreativeAnalysisPlatformLabel(platformKey) || platformKey,
        });
      });
    });
  });

  return options;
};

export const resolveCreativeAnalysisPlannedPlatforms = ({
  campaigns,
  advertiser,
  selectedCampaignName = null,
  allowAdvertiserBestFit = false,
  allowSingleApprovedFallback = true,
} = {}) => {
  const approvedFlightCampaign = selectApprovedPlanCampaign({
    campaigns,
    selectedCampaignName,
    allowAdvertiserBestFit,
    allowSingleApprovedFallback,
  });

  const approvedCampaigns = Array.isArray(campaigns)
    ? campaigns.filter(isApprovedPlanCampaign)
    : [];

  const candidateGroups = [
    approvedFlightCampaign ? [approvedFlightCampaign] : [],
    advertiser ? [advertiser] : [],
    approvedCampaigns.length ? approvedCampaigns : Array.isArray(campaigns) ? campaigns : [],
  ];

  for (const group of candidateGroups) {
    const options = resolveCreativeAnalysisPlatformOptionsFromInputs(group);
    if (options.length) return options;
  }

  return [];
};

export const selectPlannerContextCampaign = ({
  campaigns,
  selectedCampaignName,
  nowMs = Date.now(),
  allowAdvertiserBestFit = true,
  allowSingleFallback = true,
}) => {
  const available = Array.isArray(campaigns)
    ? campaigns.filter((campaign) => campaign && typeof campaign === "object")
    : [];
  if (!available.length) return null;

  const decorate = (campaign, matchType, matchConfidence, matchDetail) => ({
    ...campaign,
    __matchType: matchType,
    __matchConfidence: matchConfidence,
    __matchDetail: matchDetail,
    __sourceLabel: isApprovedPlanCampaign(campaign)
      ? "Sherloc Planner approved campaign"
      : "SherlocMediaPlanner campaign context",
  });

  if (selectedCampaignName) {
    const exact = available.find((campaign) =>
      normalizeCampaignName(campaign.campaign_name || campaign.campaignName) ===
      normalizeCampaignName(selectedCampaignName)
    );
    if (exact) {
      return decorate(
        exact,
        "exact_name",
        "high",
        "SherlocMediaPlanner context matched the selected live campaign by exact normalized name."
      );
    }

    const fuzzy = available.find((campaign) =>
      namesMatch(campaign.campaign_name || campaign.campaignName, selectedCampaignName)
    );
    if (fuzzy) {
      return decorate(
        fuzzy,
        "fuzzy_name",
        "medium",
        "SherlocMediaPlanner context matched the selected live campaign by similar normalized campaign name."
      );
    }
  }

  if (allowSingleFallback && available.length === 1) {
    return decorate(
      available[0],
      "single_planner_campaign",
      "high",
      "SherlocMediaPlanner context selected because it is the only planner campaign available for this advertiser."
    );
  }

  if (!allowAdvertiserBestFit) return null;

  const scored = available
    .map((campaign) => {
      const startMs = parseDateBoundaryMs(
        pickFirst(campaign.flight_start_date, campaign.flightStart, campaign.flight_start),
        "start"
      );
      const endMs = parseDateBoundaryMs(
        pickFirst(campaign.flight_end_date, campaign.flightEnd, campaign.flight_end),
        "end"
      );
      const hasObjective = Boolean(
        pickFirst(campaign.objective, campaign.goal_type, campaign.goalType)
      );
      const hasBudget = Boolean(
        parseBudgetValue(campaign.total_budget || campaign.totalBudget)
      );

      let score = 0;
      if (isApprovedPlanCampaign(campaign)) score += 160;
      if (campaign.countersigned_at || campaign.countersignedAt) score += 120;
      if (startMs && endMs && nowMs >= startMs && nowMs <= endMs) score += 100;
      else if (startMs && nowMs < startMs) score += 50;
      else if (endMs && nowMs > endMs) score += 25;
      if (hasObjective) score += 30;
      if (hasBudget) score += 15;
      if (startMs && endMs) score += 10;

      const recency = parseDateBoundaryMs(
        pickFirst(campaign.updated_at, campaign.updatedAt, campaign.created_at, campaign.createdAt),
        "end"
      );

      return { campaign, score, recency: recency || 0 };
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return b.recency - a.recency;
    });

  if (!scored[0]?.campaign) return null;

  return decorate(
    scored[0].campaign,
    "advertiser_best_fit",
    "medium",
    "SherlocMediaPlanner context selected as the best advertiser-level fit because no live campaign was selected."
  );
};

export const buildAdvertiserFallbackPlan = (advertiser) => {
  if (!advertiser || typeof advertiser !== "object") return null;

  const flightStart = pickFirst(
    advertiser.flightStart,
    advertiser.flight_start,
    advertiser.startDate,
    advertiser.start_date,
    advertiser.flight_start_date,
    advertiser.flightStartDate
  );

  const flightEnd = pickFirst(
    advertiser.flightEnd,
    advertiser.flight_end,
    advertiser.endDate,
    advertiser.end_date,
    advertiser.flight_end_date,
    advertiser.flightEndDate
  );

  const totalBudget = parseBudgetValue(
    pickFirst(
      advertiser.totalBudget,
      advertiser.total_budget,
      advertiser.plannedBudget,
      advertiser.planned_budget,
      advertiser.mediaBudget,
      advertiser.media_budget,
      advertiser.budget,
      advertiser.budget_total,
      advertiser.budgetTotal,
      advertiser.flightBudget,
      advertiser.flight_budget
    )
  );

  const hasDateInput = Boolean(flightStart || flightEnd);
  const hasBudgetInput = typeof totalBudget === "number";

  if (!hasDateInput && !hasBudgetInput) return null;

  return {
    source: "advertiser_flight",
    sourceLabel: "Advertiser flight data",
    campaignName: null,
    objective: null,
    flightStart,
    flightEnd,
    totalBudget,
    budgetBreakdown: null,
    financialSummary: null,
  };
};

export const buildCampaignPlanInput = (campaign) => {
  if (!campaign || typeof campaign !== "object") return null;

  return {
    source: "sherloc_planner",
    sourceLabel:
      campaign.__sourceLabel ||
      (isApprovedPlanCampaign(campaign)
        ? "Sherloc Planner approved campaign"
        : "SherlocMediaPlanner campaign context"),
    campaignId: campaign.id || null,
    campaignName: campaign.campaign_name || campaign.campaignName || null,
    objective: campaign.objective || campaign.goal_type || campaign.goalType || null,
    flightStart: pickFirst(campaign.flight_start_date, campaign.flightStart, campaign.flight_start),
    flightEnd: pickFirst(campaign.flight_end_date, campaign.flightEnd, campaign.flight_end),
    totalBudget: parseBudgetValue(pickFirst(campaign.total_budget, campaign.totalBudget, campaign.budget)),
    budgetBreakdown: campaign.budget_breakdown || campaign.budgetBreakdown || null,
    financialSummary: campaign.financial_summary || campaign.financialSummary || null,
    programmaticPricingSummary:
      campaign.programmatic_pricing_summary || campaign.programmaticPricingSummary || null,
    strategyPayloadContract: campaign.strategy_payload_contract || campaign.strategyPayloadContract || null,
    matchType: campaign.__matchType || null,
    matchConfidence: campaign.__matchConfidence || null,
    matchDetail: campaign.__matchDetail || null,
    contractStatus: pickFirst(
      campaign.contract_status,
      campaign.contractStatus,
      campaign.authorization_status,
      campaign.authorizationStatus
    ),
    rawCampaign: campaign,
  };
};

export const buildMergedApprovedPricingPlanInput = ({
  campaigns,
  preferredCampaign,
  sourceLabel = "Sherloc Planner approved campaign",
} = {}) => {
  const approvedCampaigns = (Array.isArray(campaigns) ? campaigns : []).filter(isApprovedPlanCampaign);
  if (!approvedCampaigns.length) {
    return buildCampaignPlanInput(preferredCampaign);
  }

  const orderedCampaigns = [];
  const pushCampaign = (campaign) => {
    if (!campaign || orderedCampaigns.includes(campaign)) return;
    orderedCampaigns.push(campaign);
  };

  pushCampaign(preferredCampaign);
  approvedCampaigns.forEach(pushCampaign);

  // Wave 3: pricing summaries are identity-only for client tokens
  // (lineItemKey/lineItemLabel/channel/provider). Financial pricing.* reads
  // are retired — dedupe scoring now only prefers active rows with a richer
  // identity; ties keep the first-seen (preferred campaign) row.
  const pricingItemScore = (item) => {
    const pricing = item?.pricing || item;
    const isActive = pricing?.active == null ? true : Boolean(pricing?.active);
    const hasLineItemLabel = Boolean(
      String(item?.lineItemLabel ?? item?.line_item_label ?? "").trim()
    );

    let score = 0;
    if (isActive) score += 5;
    if (hasLineItemLabel) score += 1;
    return score;
  };

  const summaryByKey = new Map();

  orderedCampaigns.forEach((campaign) => {
    const summary = Array.isArray(campaign?.programmatic_pricing_summary)
      ? campaign.programmatic_pricing_summary
      : Array.isArray(campaign?.programmaticPricingSummary)
      ? campaign.programmaticPricingSummary
      : [];

    summary.forEach((item) => {
      const dedupeKey = normalizeProgrammaticKey(
        item?.lineItemKey || item?.line_item_key || item?.channel
      );
      if (!dedupeKey) return;

      const existing = summaryByKey.get(dedupeKey);
      if (!existing || pricingItemScore(item) > pricingItemScore(existing)) {
        summaryByKey.set(dedupeKey, item);
      }
    });
  });

  const dedupedSummary = Array.from(summaryByKey.values());

  if (!dedupedSummary.length) {
    return buildCampaignPlanInput(preferredCampaign || orderedCampaigns[0]);
  }

  const baseCampaign = preferredCampaign || orderedCampaigns[0];
  const baseInput = buildCampaignPlanInput(baseCampaign) || {
    source: "sherloc_planner",
    sourceLabel,
    campaignId: null,
    campaignName: null,
    objective: null,
    flightStart: null,
    flightEnd: null,
    totalBudget: null,
    budgetBreakdown: null,
    financialSummary: null,
    strategyPayloadContract: null,
    rawCampaign: baseCampaign || null,
  };

  return {
    ...baseInput,
    sourceLabel,
    source: "sherloc_planner_merged_pricing",
    matchType: baseInput.matchType || "advertiser_best_fit",
    matchConfidence: baseInput.matchConfidence || "medium",
    matchDetail:
      baseInput.matchDetail ||
      "Approved billed pricing merged across multiple approved campaign rows for advertiser-level totals resolution.",
    programmaticPricingSummary: dedupedSummary,
    rawCampaign: {
      ...(baseInput.rawCampaign || {}),
      programmatic_pricing_summary: dedupedSummary,
    },
  };
};

export const resolveClientBilledFinancialTruth = ({
  planInput,
  platformKey,
  liveMetrics,
  liveMetricsByPlatform,
  liveAttributionByPlatform,
  liveDataSourcesByPlatform,
  liveDataSource,
} = {}) => {
  const buildUnavailable = ({
    source = "approved_campaign_pricing",
    reasonCode,
    reasonDetail,
    contributingPlatforms = [],
    missingPlatforms = [],
    impressionsAvailable = false,
    denominatorAvailable = false,
    attributionAvailable = true,
    contributorAudit = [],
  } = {}) => ({
    available: false,
    source,
    reason: reasonDetail,
    reasonCode: reasonCode || null,
    reasonDetail: reasonDetail || null,
    billedSpend: null,
    billedCpm: null,
    billedCpc: null,
    billedCpa: null,
    contributingPlatforms,
    missingPlatforms,
    impressionsAvailable,
    denominatorAvailable,
    attributionAvailable,
    contributorAudit,
  });

  const buildAvailable = ({
    source,
    reasonCode = null,
    reasonDetail = null,
    billedSpend = null,
    billedCpm = null,
    billedCpc = null,
    billedCpa = null,
    contributingPlatforms = [],
    missingPlatforms = [],
    impressionsAvailable = false,
    denominatorAvailable = false,
    attributionAvailable = true,
    contributorAudit = [],
  } = {}) => ({
    available: true,
    source,
    reason: reasonDetail,
    reasonCode,
    reasonDetail,
    billedSpend,
    billedCpm,
    billedCpc,
    billedCpa,
    contributingPlatforms,
    missingPlatforms,
    impressionsAvailable,
    denominatorAvailable,
    attributionAvailable,
    contributorAudit,
  });

  const normalizedPlatformKey = normalizeProgrammaticKey(platformKey);
  if (!normalizedPlatformKey) {
    return buildUnavailable({
      source: "approved_campaign_pricing",
      reasonCode: "invalid_scope",
      reasonDetail: "Client billed delivery could not be resolved because the current Optimus scope is invalid.",
      attributionAvailable: false,
    });
  }

  const summary = Array.isArray(planInput?.programmaticPricingSummary)
    ? planInput.programmaticPricingSummary
    : Array.isArray(planInput?.rawCampaign?.programmatic_pricing_summary)
    ? planInput.rawCampaign.programmatic_pricing_summary
    : Array.isArray(planInput?.rawCampaign?.programmaticPricingSummary)
    ? planInput.rawCampaign.programmaticPricingSummary
    : [];

  // Wave 3: identity-only match. Post-strip summary rows carry only
  // lineItemKey/lineItemLabel/channel/provider labels — matching never
  // requires (or reads) a pricing model. Used for audit nuance and labels.
  const findMatchedLineItem = (platformValue) =>
    summary.find((item) => {
      const normalizedKey = normalizeProgrammaticKey(platformValue);
      const lineItemKey = normalizeProgrammaticKey(item?.lineItemKey || item?.line_item_key);
      const channel = normalizeProgrammaticKey(item?.channel);
      const pricing = item?.pricing || item;
      const isActive = pricing?.active == null ? true : Boolean(pricing?.active);

      return isActive && (lineItemKey === normalizedKey || channel === normalizedKey);
    });

  // Wave 3 contract: the browser-side billed engine is retired. Billed truth
  // is built ONLY from server-declared billed fields already present on the
  // live metrics row (`billedSpend`/`clientFacingSpend`, plus served
  // `billedCpm`/`billedCpc`/`billedCpa` where present) — never recomputed from
  // provider `spend` plus pricing configuration (which is no longer served to
  // client tokens). When the server declared no billed basis, this returns
  // null and the caller reports unavailable — no provider/net fallback.
  const resolveServedBilledMetrics = (metrics, platformValue) => {
    const billedSpend = parseBudgetValue(
      metrics?.billedSpend ?? metrics?.clientFacingSpend
    );
    const impressions = parseBudgetValue(metrics?.impressions);
    const clicks = parseBudgetValue(metrics?.clicks);
    const conversions = parseBudgetValue(metrics?.conversions);

    if (billedSpend == null) return null;

    const servedBilledCpm = parseBudgetValue(
      metrics?.billedCpm ?? metrics?.clientFacingCpm
    );
    const servedBilledCpc = parseBudgetValue(metrics?.billedCpc);
    const servedBilledCpa = parseBudgetValue(metrics?.billedCpa);
    const servedBasis = String(metrics?.spendBasis || "").trim().toLowerCase();

    return {
      platformKey: normalizeProgrammaticKey(platformValue),
      pricingModel: servedBasis || "server_billed",
      billedSpend,
      billedCpm:
        servedBilledCpm != null
          ? servedBilledCpm
          : typeof impressions === "number" && impressions > 0
          ? (billedSpend / impressions) * 1000
          : null,
      billedCpc:
        servedBilledCpc != null
          ? servedBilledCpc
          : typeof clicks === "number" && clicks > 0
          ? billedSpend / clicks
          : null,
      billedCpa:
        servedBilledCpa != null
          ? servedBilledCpa
          : typeof conversions === "number" && conversions > 0
          ? billedSpend / conversions
          : null,
      impressions,
      clicks,
      conversions,
    };
  };

  // New DSPs use only server-declared billed observations. Their provider/net
  // totals and StackAdapt pricing configuration are never client billing evidence.
  const resolveProviderBilledMetrics = (metrics, key) => {
    const number = (value) => value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : null;
    const billedSpend = governedProviderSpend(metrics);
    if (billedSpend === null) return null;
    return {
      platformKey: key, pricingModel: "server_billed", billedSpend,
      billedCpm: number(metrics?.billedCpm ?? metrics?.clientFacingCpm),
      billedCpc: number(metrics?.billedCpc), billedCpa: number(metrics?.billedCpa),
      impressions: number(metrics?.impressions), clicks: number(metrics?.clicks), conversions: number(metrics?.conversions),
    };
  };

  const resolveDirectClientSpend = (metrics, platformValue) => {
    // Direct social platforms: the served spend IS the client-billed figure
    // (declared client billed). Prefer an explicit served billed field when
    // the payload carries one; never any provider/net alias.
    const spend = parseBudgetValue(
      metrics?.billedSpend ?? metrics?.clientFacingSpend ?? metrics?.spend
    );
    const impressions = parseBudgetValue(metrics?.impressions);
    const clicks = parseBudgetValue(metrics?.clicks);
    const conversions = parseBudgetValue(metrics?.conversions);

    if (spend == null) return null;

    return {
      platformKey: normalizeProgrammaticKey(platformValue),
      pricingModel: "direct_client_spend",
      billedSpend: spend,
      billedCpm:
        typeof impressions === "number" && impressions > 0 ? (spend / impressions) * 1000 : null,
      billedCpc:
        typeof clicks === "number" && clicks > 0 ? spend / clicks : null,
      billedCpa:
        typeof conversions === "number" && conversions > 0 ? spend / conversions : null,
      impressions,
      clicks,
      conversions,
    };
  };

  const resolveExactIngestedBilledSpend = (metrics, platformValue) => {
    const resolved = resolveDirectClientSpend(metrics, platformValue);
    if (!resolved || typeof resolved.billedSpend !== "number") return null;
    return {
      ...resolved,
      pricingModel: "ingested_client_billed_spend",
    };
  };

  if (normalizedPlatformKey === "all") {
    const byPlatform =
      liveMetricsByPlatform && typeof liveMetricsByPlatform === "object" ? liveMetricsByPlatform : {};
    const attributionByPlatform =
      liveAttributionByPlatform && typeof liveAttributionByPlatform === "object"
        ? liveAttributionByPlatform
        : {};
    const dataSourcesByPlatform =
      liveDataSourcesByPlatform && typeof liveDataSourcesByPlatform === "object"
        ? liveDataSourcesByPlatform
        : {};
    const observedPlatformKeys = Object.keys(byPlatform)
      .map((key) => normalizeProgrammaticKey(key))
      .filter(Boolean);
    const attributionAvailable = Object.keys(attributionByPlatform).length > 0;

    const contributingKeys = Object.entries(byPlatform)
      .filter(([, metrics]) =>
        ["spend", "impressions", "clicks", "conversions", "billedSpend", "clientFacingSpend"].some((key) => {
          const value = metrics?.[key];
          return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));
        })
      )
      .map(([key]) => normalizeProgrammaticKey(key))
      .filter(Boolean);

    if (!contributingKeys.length) {
      return buildUnavailable({
        source: "approved_campaign_pricing",
        reasonCode: attributionAvailable ? "no_safe_billed_truth" : "missing_client_billing_attribution",
        reasonDetail: attributionAvailable
          ? "Approved plan exists, but this Optimus view does not yet have channel-level live metrics needed to derive billed delivery."
          : "Approved plan exists, but client billing attribution metadata is missing for the current Optimus contributors.",
        contributingPlatforms: [],
        missingPlatforms: observedPlatformKeys,
        attributionAvailable,
      });
    }

    const lineItemResults = [];
    const unsupportedKeys = [];
    const excludedDirectKeys = [];
    const contributorAudit = [];
    for (const key of contributingKeys) {
      const metrics = byPlatform[key] || {};
      const dataSource = String(dataSourcesByPlatform?.[key] || "")
        .trim()
        .toLowerCase();
      const mode = String(
        attributionByPlatform?.[key]?.clientBilledMode ||
          attributionByPlatform?.[key]?.client_billed_mode ||
          ""
      )
        .trim()
        .toLowerCase();

      if (normalizeProviderSurfaceKey(key)) {
        const result = resolveProviderBilledMetrics(metrics, key);
        if (result) lineItemResults.push(result);
        else unsupportedKeys.push(key);
        contributorAudit.push({ platformKey: key, mode: "server_billed", included: Boolean(result), billedSpend: result?.billedSpend ?? null, reasonCode: result ? "billed_truth_available" : "missing_billing_provenance" });
        continue;
      }

      if (mode === "direct_client_spend" || DIRECT_CLIENT_SPEND_KEYS.has(key)) {
        const directResult = resolveDirectClientSpend(metrics, key);
        if (!directResult || typeof directResult.billedSpend !== "number") {
          excludedDirectKeys.push(key);
          contributorAudit.push({
            platformKey: key,
            mode: "direct_client_spend",
            included: false,
            reasonCode: "contributor_missing",
            reasonDetail:
              "Direct client spend contributor did not have a usable spend basis in the current live window.",
            liveSpend: parseBudgetValue(metrics?.spend),
            liveImpressions: parseBudgetValue(metrics?.impressions),
            billedSpend: null,
            pricingModel: "direct_client_spend",
          });
          continue;
        }
        lineItemResults.push(directResult);
        contributorAudit.push({
          platformKey: key,
          mode: "direct_client_spend",
          included: true,
          reasonCode: "billed_truth_available",
          reasonDetail: "Direct client spend contributor included in billed rollup.",
          liveSpend: parseBudgetValue(metrics?.spend),
          liveImpressions: parseBudgetValue(metrics?.impressions),
          billedSpend: directResult.billedSpend,
          pricingModel: "direct_client_spend",
        });
        continue;
      }

      const isProgrammaticPriced =
        mode === "approved_programmatic_pricing" || PROGRAMMATIC_PLATFORM_KEYS.has(key);
      if (!isProgrammaticPriced) {
        unsupportedKeys.push(key);
        contributorAudit.push({
          platformKey: key,
          mode: mode || "unsupported",
          included: false,
          reasonCode: "mixed_scope_unsafe",
          reasonDetail:
            "Contributor does not currently have a shared client-billed attribution rule and was excluded from billed rollup.",
          liveSpend: parseBudgetValue(metrics?.spend),
          liveImpressions: parseBudgetValue(metrics?.impressions),
          billedSpend: null,
          pricingModel: null,
        });
        continue;
      }

      if (dataSource === "csv_console") {
        const exactSnapshotResult = resolveExactIngestedBilledSpend(metrics, key);
        if (exactSnapshotResult && typeof exactSnapshotResult.billedSpend === "number") {
          lineItemResults.push(exactSnapshotResult);
          contributorAudit.push({
            platformKey: key,
            mode: "approved_programmatic_pricing",
            included: true,
            reasonCode: "billed_truth_available",
            reasonDetail:
              "Exact-range ingested client-billed snapshot contributor included in billed rollup.",
            liveSpend: parseBudgetValue(metrics?.spend),
            liveImpressions: parseBudgetValue(metrics?.impressions),
            billedSpend: exactSnapshotResult.billedSpend,
            pricingModel: exactSnapshotResult.pricingModel,
          });
          continue;
        }
      }

      // Wave 3: billed truth comes ONLY from the server-declared billed
      // fields on the live row — never from provider spend plus pricing
      // config. The identity row (when present) only refines the audit
      // reason for an unavailable contributor.
      const result = resolveServedBilledMetrics(metrics, key);
      if (!result || typeof result.billedSpend !== "number") {
        const matchedLineItem = findMatchedLineItem(key);
        unsupportedKeys.push(key);
        contributorAudit.push({
          platformKey: key,
          mode: "approved_programmatic_pricing",
          included: false,
          reasonCode: matchedLineItem ? "contributor_missing" : "approved_plan_mismatch",
          reasonDetail: matchedLineItem
            ? "The server declared no billed basis for this programmatic contributor in the current live window."
            : "No active approved pricing row matched this live programmatic contributor.",
          liveSpend: parseBudgetValue(metrics?.spend),
          liveImpressions: parseBudgetValue(metrics?.impressions),
          billedSpend: null,
          pricingModel: null,
        });
        continue;
      }

      lineItemResults.push(result);
      contributorAudit.push({
        platformKey: key,
        mode: "approved_programmatic_pricing",
        included: true,
        reasonCode: "billed_truth_available",
        reasonDetail: "Server-declared billed programmatic contributor included in billed rollup.",
        liveSpend: parseBudgetValue(metrics?.spend),
        liveImpressions: parseBudgetValue(metrics?.impressions),
        billedSpend: result.billedSpend,
        pricingModel: result.pricingModel,
      });
    }

    if (!lineItemResults.length) {
      const excludedContributorLabels = [...excludedDirectKeys, ...unsupportedKeys].join(", ");
      return buildUnavailable({
        source: "approved_campaign_pricing",
        reasonCode: "no_safe_billed_truth",
        reasonDetail: excludedContributorLabels
          ? `Approved plan exists, but none of the current all-platform contributors could be translated into safe advertiser-facing billed delivery for this window. Contributors without a usable billed basis were excluded: ${excludedContributorLabels}.`
          : "Approved plan exists, but none of the current all-platform contributors could be translated into safe advertiser-facing billed delivery for this window.",
        contributingPlatforms: [],
        missingPlatforms: [...excludedDirectKeys, ...unsupportedKeys],
        attributionAvailable,
        contributorAudit,
      });
    }

    const providerBillingMissing = unsupportedKeys.some(normalizeProviderSurfaceKey);
    const observedCurrencies = new Set(Object.values(byPlatform).map((metrics) => metrics?.currency).filter(Boolean));
    if (providerBillingMissing || (contributingKeys.some(normalizeProviderSurfaceKey) && (observedCurrencies.size > 1 || !observedCurrencies.has("USD")))) {
      return buildUnavailable({ source: "server_billed", reasonCode: providerBillingMissing ? "missing_billing_provenance" : "currency_mismatch", reasonDetail: "Complete client-billed spend is unavailable for this reporting scope.", missingPlatforms: unsupportedKeys, contributorAudit });
    }
    const billedSpend = lineItemResults.reduce((sum, item) => sum + (item.billedSpend || 0), 0);
    const impressions = lineItemResults.reduce((sum, item) => sum + (item.impressions || 0), 0);
    const clicks = lineItemResults.reduce((sum, item) => sum + (item.clicks || 0), 0);
    const conversions = lineItemResults.reduce((sum, item) => sum + (item.conversions || 0), 0);
    const excludedContributorLabels = [...excludedDirectKeys, ...unsupportedKeys].join(", ");

    return buildAvailable({
      source: "approved_campaign_pricing_rollup",
      reasonCode: excludedContributorLabels ? "partial_rollup_supported" : "billed_truth_available",
      reasonDetail: excludedContributorLabels
        ? `Live advertiser-facing values are derived from direct social spend plus approved campaign pricing across supported contributors. Contributors without a usable billed basis were excluded from billed totals: ${excludedContributorLabels}.`
        : "Live advertiser-facing values are derived from direct social spend plus approved campaign pricing across supported contributors.",
      billedSpend,
      billedCpm: impressions > 0 ? (billedSpend / impressions) * 1000 : null,
      billedCpc: clicks > 0 ? billedSpend / clicks : null,
      billedCpa: conversions > 0 ? billedSpend / conversions : null,
      contributingPlatforms: lineItemResults.map((item) => item.platformKey).filter(Boolean),
      missingPlatforms: [...excludedDirectKeys, ...unsupportedKeys],
      impressionsAvailable: impressions > 0,
      denominatorAvailable: billedSpend > 0,
      attributionAvailable,
      contributorAudit,
    });
  }

  if (normalizeProviderSurfaceKey(normalizedPlatformKey)) {
    const result = resolveProviderBilledMetrics(liveMetrics, normalizedPlatformKey);
    if (!result) return buildUnavailable({ source: "server_billed", reasonCode: "missing_billing_provenance", reasonDetail: "Client-billed spend is unavailable for this provider and reporting window.", missingPlatforms: [normalizedPlatformKey] });
    return buildAvailable({ ...result, source: "server_billed", contributingPlatforms: [normalizedPlatformKey], impressionsAvailable: result.impressions !== null, denominatorAvailable: result.billedSpend > 0 });
  }

  const singlePlatformMode = String(
    liveAttributionByPlatform?.[normalizedPlatformKey]?.clientBilledMode ||
      liveAttributionByPlatform?.[normalizedPlatformKey]?.client_billed_mode ||
      ""
  )
    .trim()
    .toLowerCase();

  if (singlePlatformMode === "direct_client_spend" || DIRECT_CLIENT_SPEND_KEYS.has(normalizedPlatformKey)) {
    const resolved = resolveDirectClientSpend(liveMetrics, normalizedPlatformKey);
    if (!resolved || typeof resolved.billedSpend !== "number") {
      return buildUnavailable({
        source: "direct_client_spend",
        reasonCode: "contributor_missing",
        reasonDetail:
          "Approved plan exists, but the current social platform does not have enough live billed delivery data for this window.",
        missingPlatforms: [normalizedPlatformKey],
      });
    }

    return buildAvailable({
      source: "direct_client_spend",
      reasonCode: "billed_truth_available",
      reasonDetail: "Live advertiser-facing values are using direct social spend for the current platform.",
      billedSpend: resolved.billedSpend,
      billedCpm: resolved.billedCpm,
      billedCpc: resolved.billedCpc,
      billedCpa: resolved.billedCpa,
      contributingPlatforms: [normalizedPlatformKey],
      impressionsAvailable: typeof resolved.impressions === "number" && resolved.impressions > 0,
      denominatorAvailable: typeof resolved.billedSpend === "number" && resolved.billedSpend > 0,
    });
  }

  if (
    singlePlatformMode !== "approved_programmatic_pricing" &&
    !PROGRAMMATIC_PLATFORM_KEYS.has(normalizedPlatformKey)
  ) {
    return buildUnavailable({
      source: "approved_campaign_pricing",
      reasonCode: "mixed_scope_unsafe",
      reasonDetail:
        "Approved plan exists, but this selection does not have advertiser-facing billed pricing inputs for live delivery.",
      missingPlatforms: [normalizedPlatformKey],
    });
  }

  const normalizedLiveDataSource = String(liveDataSource || "").trim().toLowerCase();
  if (normalizedLiveDataSource === "csv_console") {
    const exactSnapshotResolved = resolveExactIngestedBilledSpend(liveMetrics, normalizedPlatformKey);
    if (exactSnapshotResolved && typeof exactSnapshotResolved.billedSpend === "number") {
      return buildAvailable({
        source: "ingested_client_billed_snapshot",
        reasonCode: "billed_truth_available",
        reasonDetail:
          "Live advertiser-facing values are using the exact-range ingested client-billed snapshot for this platform.",
        billedSpend: exactSnapshotResolved.billedSpend,
        billedCpm: exactSnapshotResolved.billedCpm,
        billedCpc: exactSnapshotResolved.billedCpc,
        billedCpa: exactSnapshotResolved.billedCpa,
        contributingPlatforms: [normalizedPlatformKey],
        impressionsAvailable:
          typeof exactSnapshotResolved.impressions === "number" && exactSnapshotResolved.impressions > 0,
        denominatorAvailable:
          typeof exactSnapshotResolved.billedSpend === "number" && exactSnapshotResolved.billedSpend > 0,
      });
    }
  }

  // Wave 3: billed truth for a priced platform comes ONLY from the
  // server-declared billed fields on the live metrics row. When the server
  // declared no billed basis, the result is unavailable — never a
  // recomputation from provider spend plus pricing config, never a raw
  // fallback. The identity row (when present) only refines the reason.
  const resolved = resolveServedBilledMetrics(liveMetrics, normalizedPlatformKey);
  if (!resolved || typeof resolved.billedSpend !== "number") {
    const matchedLineItem = findMatchedLineItem(normalizedPlatformKey);
    if (!matchedLineItem) {
      return buildUnavailable({
        source: "approved_campaign_pricing",
        reasonCode: "approved_plan_mismatch",
        reasonDetail:
          "Approved plan exists, but the current platform does not have an active billed pricing line item on the approved campaign.",
        missingPlatforms: [normalizedPlatformKey],
      });
    }

    return buildUnavailable({
      source: "approved_campaign_pricing",
      reasonCode: "contributor_missing",
      reasonDetail:
        "Approved plan exists, but the server has not declared billed advertiser-facing values for this platform in this window.",
      missingPlatforms: [normalizedPlatformKey],
    });
  }

  return buildAvailable({
    source: "approved_campaign_pricing",
    reasonCode: "billed_truth_available",
    reasonDetail: "Live advertiser-facing values are using server-declared billed delivery for the current platform.",
    billedSpend: resolved.billedSpend,
    billedCpm: resolved.billedCpm,
    billedCpc: resolved.billedCpc,
    billedCpa: resolved.billedCpa,
    contributingPlatforms: [normalizedPlatformKey],
    impressionsAvailable: typeof resolved.impressions === "number" && resolved.impressions > 0,
    denominatorAvailable: typeof resolved.billedSpend === "number" && resolved.billedSpend > 0,
  });
};

export const deriveClientFacingEfficiencyMeta = ({
  isAllPlatformsView,
  clientFacingSpend,
  clientFacingCpm,
  impressions,
  clientFinancialTruth,
} = {}) => {
  if (!isAllPlatformsView) {
    return {
      available: typeof clientFacingCpm === "number",
      reasonCode:
        typeof clientFacingCpm === "number"
          ? "billed_truth_available"
          : clientFinancialTruth?.reasonCode || null,
      reasonDetail:
        typeof clientFacingCpm === "number"
          ? null
          : clientFinancialTruth?.reasonDetail || clientFinancialTruth?.reason || null,
      impressionsAvailable: clientFinancialTruth?.impressionsAvailable ?? false,
    };
  }

  if (typeof clientFacingSpend !== "number") {
    return {
      available: false,
      reasonCode: clientFinancialTruth?.reasonCode || "no_safe_billed_truth",
      reasonDetail: clientFinancialTruth?.reasonDetail || clientFinancialTruth?.reason || null,
      impressionsAvailable: false,
    };
  }

  if (typeof impressions !== "number") {
    return {
      available: false,
      reasonCode: "missing_impressions",
      reasonDetail:
        "Client-facing billed spend is available, but eCPM is unavailable because impressions are missing for this Optimus scope.",
      impressionsAvailable: false,
    };
  }

  if (impressions <= 0) {
    return {
      available: false,
      reasonCode: "zero_impressions",
      reasonDetail:
        "Client-facing billed spend is available, but eCPM is unavailable because impressions are zero for this Optimus scope.",
      impressionsAvailable: false,
    };
  }

  return {
    available: true,
    reasonCode: "billed_truth_available",
    reasonDetail: null,
    impressionsAvailable: true,
  };
};

export const deriveClientFacingSpendValue = (clientFinancialTruth) =>
  clientFinancialTruth?.available && typeof clientFinancialTruth?.billedSpend === "number"
    ? clientFinancialTruth.billedSpend
    : null;

export const deriveClientFacingTotalSpendAllValue = (clientAllFinancialTruth) =>
  clientAllFinancialTruth?.available && typeof clientAllFinancialTruth?.billedSpend === "number"
    ? clientAllFinancialTruth.billedSpend
    : null;

export const deriveClientFacingTotalSpendAllMeta = ({
  clientAllFinancialTruth,
  clientFacingTotalSpendAll,
} = {}) => ({
  available: typeof clientFacingTotalSpendAll === "number",
  reasonCode:
    typeof clientFacingTotalSpendAll === "number"
      ? "billed_truth_available"
      : clientAllFinancialTruth?.reasonCode || "missing_all_platform_denominator",
  reasonDetail:
    typeof clientFacingTotalSpendAll === "number"
      ? null
      : clientAllFinancialTruth?.reasonDetail || clientAllFinancialTruth?.reason || null,
  denominatorAvailable: clientAllFinancialTruth?.denominatorAvailable ?? false,
  contributingPlatforms: clientAllFinancialTruth?.contributingPlatforms || [],
  missingPlatforms: clientAllFinancialTruth?.missingPlatforms || [],
});

export const deriveClientFacingSpendShareValue = ({
  clientFacingSpend,
  clientFacingTotalSpendAll,
} = {}) => {
  if (
    typeof clientFacingSpend !== "number" ||
    typeof clientFacingTotalSpendAll !== "number" ||
    clientFacingTotalSpendAll <= 0
  ) {
    return null;
  }

  const pct = (clientFacingSpend / clientFacingTotalSpendAll) * 100;
  return Number.isFinite(pct) ? pct : null;
};

export const deriveClientFacingSpendShareMeta = ({
  clientFacingSpend,
  clientFacingTotalSpendAll,
  clientFacingTotalSpendAllMeta,
  clientFinancialTruth,
} = {}) => {
  if (typeof clientFacingSpend !== "number") {
    return {
      available: false,
      reasonCode: clientFinancialTruth?.reasonCode || "no_safe_billed_truth",
      reasonDetail: clientFinancialTruth?.reasonDetail || clientFinancialTruth?.reason || null,
    };
  }
  if (typeof clientFacingTotalSpendAll !== "number") {
    return {
      available: false,
      reasonCode:
        clientFacingTotalSpendAllMeta?.reasonCode || "missing_all_platform_denominator",
      reasonDetail:
        clientFacingTotalSpendAllMeta?.reasonDetail ||
        "Spend share is unavailable because the billed all-platform denominator is missing.",
    };
  }
  if (clientFacingTotalSpendAll <= 0) {
    return {
      available: false,
      reasonCode: "missing_all_platform_denominator",
      reasonDetail:
        "Spend share is unavailable because the billed all-platform denominator is zero for this Optimus scope.",
    };
  }
  return {
    available: true,
    reasonCode: "billed_truth_available",
    reasonDetail: null,
  };
};

export const deriveClientFacingDailySpendValue = ({
  clientFacingSpend,
  selectedRangeDayCount,
  activeFlightDayCount,
  elapsedActiveFlightDays,
} = {}) => {
  if (typeof clientFacingSpend !== "number" || !Number.isFinite(clientFacingSpend)) return null;
  const dayCount = Number.isFinite(activeFlightDayCount)
    ? activeFlightDayCount
    : Number.isFinite(elapsedActiveFlightDays)
    ? elapsedActiveFlightDays
    : selectedRangeDayCount;
  if (!Number.isFinite(dayCount) || dayCount <= 0) return null;
  return clientFacingSpend / Math.max(1, dayCount);
};

export const deriveDailySpendMeta = ({
  clientFacingSpend,
  clientFinancialTruth,
  selectedRangeDayCount,
  activeFlightDayCount,
  elapsedActiveFlightDays,
} = {}) => {
  if (typeof clientFacingSpend !== "number" || !Number.isFinite(clientFacingSpend)) {
    return {
      available: false,
      reasonCode: clientFinancialTruth?.reasonCode || "no_safe_billed_truth",
      reasonDetail: clientFinancialTruth?.reasonDetail || clientFinancialTruth?.reason || null,
    };
  }
  const dayCount = Number.isFinite(activeFlightDayCount)
    ? activeFlightDayCount
    : Number.isFinite(elapsedActiveFlightDays)
    ? elapsedActiveFlightDays
    : selectedRangeDayCount;
  if (!Number.isFinite(dayCount) || dayCount <= 0) {
    return {
      available: false,
      reasonCode: "invalid_selected_range",
      reasonDetail:
        "Daily billed spend is unavailable because the current Optimus active-flight window does not resolve to a usable day count.",
    };
  }
  return {
    available: true,
    reasonCode: "billed_truth_available",
    reasonDetail: null,
  };
};

export const buildCanonicalPacing = ({
  planInput,
  advertiserFallbackPlan,
  selectedCampaignName,
  liveSpend,
  nowMs = Date.now(),
  options = {},
}) => {
  const sourcePlan = planInput || advertiserFallbackPlan || null;
  const liveSpendNum = typeof liveSpend === "number" && Number.isFinite(liveSpend) ? liveSpend : null;

  if (!sourcePlan) {
    return {
      hasPlan: false,
      reason:
        selectedCampaignName
          ? "Pacing plan unavailable: no approved Sherloc Planner plan matched the selected campaign, and advertiser flight data was not found."
          : "Pacing plan unavailable: no approved Sherloc Planner plan or advertiser flight data found.",
      source: null,
      sourceLabel: null,
      flightStart: null,
      flightEnd: null,
      totalBudget: null,
      grossBudget: null,
      netMediaBudget: null,
      marginDollars: null,
      marginPercent: null,
      budgetSource: null,
      grossBudgetSource: null,
      netBudgetSource: null,
      marginSource: null,
      breakdownTotal: null,
      usedBreakdownAsFullBudget: false,
      usedMarginAssumption: false,
      assumedMarginPercent: null,
      pacingBudget: null,
      pacingTargetLabel: null,
      calculationReason: "No reliable approved budget basis was available for pacing.",
      elapsedDays: null,
      totalFlightDays: null,
      elapsedPct: null,
      liveSpend: liveSpendNum,
      plannedSpendToDate: null,
      pacingDelta: null,
      pacingPctVsPlan: null,
      pacingStatus: "plan unavailable",
      pacingLabel: "Plan Unavailable",
      pacingTone: "neutral",
      flightLabel: "—",
      detailLines: [],
    };
  }

  const flightStart = toDateOnlyString(sourcePlan.flightStart);
  const flightEnd = toDateOnlyString(sourcePlan.flightEnd);
  const budgetStructure = resolvePlanBudgetStructure(sourcePlan, options);
  const grossBudget = budgetStructure.grossBudget;
  const netMediaBudget = budgetStructure.netMediaBudget;
  const marginDollars = budgetStructure.marginDollars;
  const marginPercent = budgetStructure.marginPercent;
  const totalBudget = grossBudget;
  const pacingBudget = budgetStructure.pacingBudget;

  if (!flightStart || !flightEnd) {
    return {
      hasPlan: false,
      reason: "Pacing plan unavailable: approved flight dates missing.",
      source: sourcePlan.source || null,
      sourceLabel: sourcePlan.sourceLabel || null,
      flightStart,
      flightEnd,
      totalBudget,
      grossBudget,
      netMediaBudget,
      marginDollars,
      marginPercent,
      budgetSource: budgetStructure.budgetSource || null,
      grossBudgetSource: budgetStructure.grossBudgetSource || null,
      netBudgetSource: budgetStructure.netBudgetSource || null,
      marginSource: budgetStructure.marginSource || null,
      breakdownTotal: budgetStructure.breakdownTotal ?? null,
      usedBreakdownAsFullBudget: budgetStructure.usedBreakdownAsFullBudget || false,
      usedMarginAssumption: budgetStructure.usedMarginAssumption || false,
      assumedMarginPercent: budgetStructure.assumedMarginPercent ?? null,
      pacingBudget,
      pacingTargetLabel: budgetStructure.pacingTargetLabel || null,
      calculationReason: budgetStructure.calculationReason,
      elapsedDays: null,
      totalFlightDays: null,
      elapsedPct: null,
      liveSpend: liveSpendNum,
      plannedSpendToDate: null,
      pacingDelta: null,
      pacingPctVsPlan: null,
      pacingStatus: "plan unavailable",
      pacingLabel: "Plan Unavailable",
      pacingTone: "neutral",
      flightLabel: "—",
      detailLines: [],
    };
  }

  if (typeof pacingBudget !== "number") {
    return {
      hasPlan: false,
      reason: "Pacing plan unavailable: approved total budget missing.",
      source: sourcePlan.source || null,
      sourceLabel: sourcePlan.sourceLabel || null,
      flightStart,
      flightEnd,
      totalBudget: grossBudget,
      grossBudget,
      netMediaBudget,
      marginDollars,
      marginPercent,
      budgetSource: budgetStructure.budgetSource || null,
      grossBudgetSource: budgetStructure.grossBudgetSource || null,
      netBudgetSource: budgetStructure.netBudgetSource || null,
      marginSource: budgetStructure.marginSource || null,
      breakdownTotal: budgetStructure.breakdownTotal ?? null,
      usedBreakdownAsFullBudget: budgetStructure.usedBreakdownAsFullBudget || false,
      usedMarginAssumption: budgetStructure.usedMarginAssumption || false,
      assumedMarginPercent: budgetStructure.assumedMarginPercent ?? null,
      pacingBudget: null,
      pacingTargetLabel: budgetStructure.pacingTargetLabel || null,
      calculationReason: budgetStructure.calculationReason,
      elapsedDays: null,
      totalFlightDays: null,
      elapsedPct: null,
      liveSpend: liveSpendNum,
      plannedSpendToDate: null,
      pacingDelta: null,
      pacingPctVsPlan: null,
      pacingStatus: "plan unavailable",
      pacingLabel: "Plan Unavailable",
      pacingTone: "neutral",
      flightLabel: `${flightStart} → ${flightEnd}`,
      detailLines: [],
    };
  }

  const startMs = parseDateBoundaryMs(flightStart, "start");
  const endMs = parseDateBoundaryMs(flightEnd, "end");
  if (!startMs || !endMs || endMs < startMs) {
    return {
      hasPlan: false,
      reason: "Pacing plan unavailable: approved flight dates are invalid.",
      source: sourcePlan.source || null,
      sourceLabel: sourcePlan.sourceLabel || null,
      flightStart,
      flightEnd,
      totalBudget,
      grossBudget,
      netMediaBudget,
      marginDollars,
      marginPercent,
      budgetSource: budgetStructure.budgetSource || null,
      grossBudgetSource: budgetStructure.grossBudgetSource || null,
      netBudgetSource: budgetStructure.netBudgetSource || null,
      marginSource: budgetStructure.marginSource || null,
      breakdownTotal: budgetStructure.breakdownTotal ?? null,
      usedBreakdownAsFullBudget: budgetStructure.usedBreakdownAsFullBudget || false,
      usedMarginAssumption: budgetStructure.usedMarginAssumption || false,
      assumedMarginPercent: budgetStructure.assumedMarginPercent ?? null,
      pacingBudget,
      pacingTargetLabel: budgetStructure.pacingTargetLabel || null,
      calculationReason: budgetStructure.calculationReason,
      elapsedDays: null,
      totalFlightDays: null,
      elapsedPct: null,
      liveSpend: liveSpendNum,
      plannedSpendToDate: null,
      pacingDelta: null,
      pacingPctVsPlan: null,
      pacingStatus: "plan unavailable",
      pacingLabel: "Plan Unavailable",
      pacingTone: "neutral",
      flightLabel: `${flightStart} → ${flightEnd}`,
      detailLines: [],
    };
  }

  const totalFlightDays = inclusiveCalendarDays(startMs, endMs);
  const beforeStart = nowMs < startMs;
  const afterEnd = nowMs > endMs;

  let elapsedPct = 0;
  if (afterEnd) elapsedPct = 1;
  else if (!beforeStart) {
    const duration = Math.max(1, endMs - startMs);
    elapsedPct = Math.max(0, Math.min(1, (nowMs - startMs) / duration));
  }

  let elapsedDays = 0;
  if (afterEnd) elapsedDays = totalFlightDays;
  else if (!beforeStart) elapsedDays = inclusiveCalendarDays(startMs, nowMs);

  const elapsedActiveFlightDays = deriveActiveFlightDayCount({
    flightStart,
    flightEnd,
    nowMs,
  });
  const approvedDailyBudgetBasis = pacingBudget;
  const targetDailySpend =
    typeof approvedDailyBudgetBasis === "number" &&
    Number.isFinite(approvedDailyBudgetBasis) &&
    totalFlightDays > 0
      ? approvedDailyBudgetBasis / totalFlightDays
      : null;
  const actualDailySpend =
    typeof liveSpendNum === "number" &&
    Number.isFinite(liveSpendNum) &&
    Number.isFinite(elapsedActiveFlightDays) &&
    elapsedActiveFlightDays > 0
      ? liveSpendNum / elapsedActiveFlightDays
      : null;
  const dailyPacing = deriveDailyPacingStatus({
    actualDailySpend,
    targetDailySpend,
  });

  const curveSegments = extractCurveSegments(sourcePlan.budgetBreakdown, pacingBudget);
  const curvePlanned = computeCurvePlannedSpend(curveSegments, nowMs);
  const plannedSpendToDate =
    typeof curvePlanned === "number" && Number.isFinite(curvePlanned)
      ? curvePlanned
      : pacingBudget * elapsedPct;

  const pacingDelta =
    typeof liveSpendNum === "number" ? liveSpendNum - plannedSpendToDate : null;

  const pacingPctVsPlan =
    typeof liveSpendNum === "number" && plannedSpendToDate > 0
      ? (liveSpendNum / plannedSpendToDate) * 100
      : null;

  let pacingStatus = "on pace";
  let pacingLabel = "On Pace";
  let pacingTone = "ok";
  let reason =
    sourcePlan.source === "advertiser_flight"
      ? selectedCampaignName
        ? "Advertiser fallback used: approved campaign-level plan unavailable for the selected campaign."
        : "Advertiser fallback used: no approved Sherloc Planner campaign plan was selected."
      : "Approved Sherloc Planner campaign plan";

  if (beforeStart) {
    pacingStatus = "plan not started";
    pacingLabel = "Plan Not Started";
    pacingTone = "neutral";
  } else if (afterEnd) {
    pacingStatus = "flight completed";
    pacingLabel = "Flight Completed";
    pacingTone = "neutral";
  } else if (typeof pacingPctVsPlan === "number") {
    if (pacingPctVsPlan < 90) {
      pacingStatus = "behind plan";
      pacingLabel = "Behind Plan";
      pacingTone = "bad";
    } else if (pacingPctVsPlan > 110) {
      pacingStatus = "ahead of plan";
      pacingLabel = "Ahead of Plan";
      pacingTone = "good";
    }
  }

  return {
    hasPlan: true,
    reason,
    source: sourcePlan.source || null,
    sourceLabel:
      curveSegments.length && sourcePlan.source === "sherloc_planner"
        ? "Sherloc Planner approved phased plan"
        : sourcePlan.sourceLabel || null,
    campaignName: sourcePlan.campaignName || null,
    objective: sourcePlan.objective || null,
    matchType: sourcePlan.matchType || null,
    matchConfidence: sourcePlan.matchConfidence || null,
    matchDetail: sourcePlan.matchDetail || null,
    flightStart,
    flightEnd,
    totalBudget,
    grossBudget,
    netMediaBudget,
    marginDollars,
    marginPercent,
    budgetSource: budgetStructure.budgetSource || null,
    grossBudgetSource: budgetStructure.grossBudgetSource || null,
    netBudgetSource: budgetStructure.netBudgetSource || null,
    marginSource: budgetStructure.marginSource || null,
    breakdownTotal: budgetStructure.breakdownTotal ?? null,
    usedBreakdownAsFullBudget: budgetStructure.usedBreakdownAsFullBudget || false,
    usedMarginAssumption: budgetStructure.usedMarginAssumption || false,
    assumedMarginPercent: budgetStructure.assumedMarginPercent ?? null,
    pacingBudget,
    pacingTargetLabel: budgetStructure.pacingTargetLabel || null,
    calculationReason: budgetStructure.calculationReason,
    elapsedDays,
    elapsedActiveFlightDays,
    totalFlightDays,
    elapsedPct,
    liveSpend: liveSpendNum,
    plannedSpendToDate,
    targetDailySpend,
    targetDailyBudget: targetDailySpend,
    actualDailySpend,
    dailyPacingPctVsTarget: dailyPacing.dailyPacingPctVsTarget,
    dailyPacingStatus: dailyPacing.dailyPacingStatus,
    dailyPacingLabel: dailyPacing.dailyPacingLabel,
    dailyPacingTone: dailyPacing.dailyPacingTone,
    pacingDelta,
    pacingPctVsPlan,
    pacingStatus,
    pacingLabel,
    pacingTone,
    pacingModel: curveSegments.length ? "phased" : "linear",
    pacingModelDetail: curveSegments.length
      ? `Phased pacing derived from approved budget breakdown (${curveSegments.length} segments).`
      : "Linear pacing derived from approved flight dates and total budget.",
    flightLabel: `${flightStart} → ${flightEnd}`,
    detailLines: [
      `Approved Budget: ${grossBudget}`,
      `Billed Spend Basis: ${liveSpendNum}`,
      `Planned Spend to Date: ${plannedSpendToDate}`,
      `Live Spend to Date: ${liveSpendNum}`,
      `Delta vs Plan: ${pacingDelta}`,
    ],
  };
};

/*
 * Canonical Campaign Optimizer range resolution.
 *
 * ONE resolver answers "what window is this optimization actually evaluating?"
 * for every layer that needs it: the range selector, Performance Overview, the
 * optimization request payload, recommendation generation, the persisted run,
 * and the Optimization Log. No layer may re-derive a default of its own.
 *
 * The rule that matters: a FLIGHT selection that cannot be resolved to real
 * approved-flight dates STAYS FLIGHT and reports itself unresolved. It is never
 * quietly answered with the last 30 days. Substituting a different window while
 * the selector still reads FLIGHT made the run, the KPIs and the log all agree
 * with each other and all disagree with the operator — the log said "Last 30D"
 * for a run the operator asked to scope to the campaign flight.
 *
 * Only an explicit operator choice (30D / 14D / 7D) produces a preset window.
 */
export const OPTIMIZER_FLIGHT_UNRESOLVED = "approved_flight_unresolved";

export const resolveOptimizerRange = ({ presetMode, presetDatePreset, flightRange } = {}) => {
  if (presetMode === "flight") {
    const start = flightRange?.start || null;
    const end = flightRange?.end || null;

    if (start && end) {
      return {
        mode: "flight",
        rangeKey: "flight",
        startDate: start,
        endDate: end,
        datePreset: null,
        resolved: true,
        unresolvedReason: null,
      };
    }

    // Requested range is preserved. The caller must surface this and refuse to
    // evaluate rather than fall through to some other window.
    return {
      mode: "flight",
      rangeKey: "flight",
      startDate: null,
      endDate: null,
      datePreset: null,
      resolved: false,
      unresolvedReason: OPTIMIZER_FLIGHT_UNRESOLVED,
    };
  }

  return {
    mode: "preset",
    rangeKey: presetDatePreset || null,
    startDate: null,
    endDate: null,
    datePreset: presetDatePreset || null,
    resolved: true,
    unresolvedReason: null,
  };
};

/*
 * The execution truth a run persists. The Optimization Log renders from this
 * rather than recomputing a window after the fact, so a log row can never drift
 * from the range its run actually evaluated.
 */
export const buildOptimizerRunRange = ({ range, advertiserId, campaignId } = {}) => ({
  range: range?.rangeKey || null,
  mode: range?.mode || null,
  datePreset: range?.datePreset || null,
  dateStart: range?.startDate || null,
  dateEnd: range?.endDate || null,
  resolved: range?.resolved !== false,
  unresolvedReason: range?.unresolvedReason || null,
  advertiserId: advertiserId ?? null,
  campaignId: campaignId ?? null,
});

/*
 * The single vocabulary for how a campaign was matched to its approved plan.
 *
 * Both portals render this, and both previously carried their own inline
 * ladder — so a match path added to the resolver (exact_id, in the identity
 * fix) was named in neither, and the card fell through to "Unavailable" for a
 * match that was in fact governed and exact. Naming every path in one exported
 * place is what keeps the display honest to the resolver: a new __matchType
 * that is not named here fails loudly in the twin-lock test rather than quietly
 * rendering as unmatched.
 *
 * Ordered strongest to weakest.
 */
export const CAMPAIGN_MATCH_VOCABULARY = Object.freeze({
  exact_id: {
    label: "Campaign Match: Governed campaign identity",
    pillText: "Governed Identity",
    pillTone: "good",
  },
  exact_name: {
    label: "Campaign Match: Exact normalized name",
    pillText: "Exact Match",
    pillTone: "good",
  },
  fuzzy_name: {
    label: "Campaign Match: Similar normalized name",
    pillText: "Similar Match",
    pillTone: "ok",
  },
  single_approved_campaign: {
    label: "Campaign Match: Advertiser's only approved campaign",
    pillText: "Single Authorized Row",
    pillTone: "ok",
  },
  single_planner_campaign: {
    label: "Campaign Match: Advertiser's only planner campaign",
    pillText: "Single Planner Row",
    pillTone: "ok",
  },
  advertiser_best_fit: {
    label: "Campaign Match: Advertiser-level best fit",
    pillText: "Advertiser Fallback",
    pillTone: "neutral",
  },
});

const UNMATCHED_CAMPAIGN_MATCH = Object.freeze({
  label: null,
  pillText: "Unmatched",
  pillTone: "bad",
});

export const describeCampaignMatch = (matchType) =>
  CAMPAIGN_MATCH_VOCABULARY[String(matchType || "")] || UNMATCHED_CAMPAIGN_MATCH;

/*
 * ONE PACING STATUS FOR THE WHOLE PAGE.
 *
 * The grade hero rendered the backend's pacing VERDICT ("Ahead") while the
 * spend card re-derived its own label from the raw pacing score on private
 * thresholds (>= 90 "On approved pacing basis", >= 75 "Pacing requires
 * review"). A campaign running 13% ahead of the approved curve scores 87 —
 * so the same evidence read "Ahead" at the top of the page and "Pacing
 * requires review" below it, and nothing on screen let a reader tell which
 * was true.
 *
 * A score states DISTANCE from the approved curve and says nothing about
 * which side of it. Only the backend's verdict does, so the verdict is the
 * label, and "requires review" is kept as a SEPARATE claim carrying its own
 * reason (spend past the approved authorization, or a spend basis that
 * cannot be compared with the approved curve at all). Being off-curve inside
 * the authorization is reported as ahead or behind and never escalated.
 *
 * Backend authority: presentation.pacingStatus (optimus_pacing_status_v1) in
 * services/optimusUnifiedCampaignGradingService.js.
 */
export const resolveOptimusPacingStatus = (optimusGrading) => {
  const governed = optimusGrading?.presentation?.pacingStatus;
  if (governed?.label) {
    return {
      label: governed.reviewRequired ? `${governed.label} — review required` : governed.label,
      tone: governed.reviewRequired ? "ok" : governed.tone || "neutral",
      title: governed.reviewReason || governed.summary || undefined,
      verdict: governed.verdict || null,
      reviewRequired: governed.reviewRequired === true,
      governed: true,
    };
  }

  /*
   * Deploy-gap fallback only, for a payload published before the contract
   * existed. It states ahead/behind from the ratio — the one thing the raw
   * pacing block can honestly answer — and never invents a review band.
   */
  const pacing = optimusGrading?.financialDimensions?.pacing;
  const lifecycle = optimusGrading?.lifecycle?.state;
  if (!pacing || pacing.state !== "EVALUATED" || typeof pacing.score !== "number") {
    if (lifecycle === "PRE_LIVE") {
      return { label: "Plan Not Started", tone: "neutral", governed: false, reviewRequired: false };
    }
    if (lifecycle === "AWAITING_DELIVERY") {
      return { label: "Spend Pending", tone: "neutral", governed: false, reviewRequired: false };
    }
    return { label: "Pacing not established", tone: "neutral", governed: false, reviewRequired: false };
  }
  const ratio = pacing.pacingRatio;
  const label = !Number.isFinite(ratio)
    ? "Pacing not established"
    : ratio > 1
    ? "Ahead"
    : ratio < 1
    ? "Behind"
    : "On pace";
  return {
    label,
    tone: label === "Behind" ? "ok" : label === "Pacing not established" ? "neutral" : "good",
    governed: false,
    reviewRequired: false,
  };
};
